import { Injectable, NgZone } from '@angular/core';
import { DeleteOptions } from './delete-options';
import { CreateOptions } from './create-options';
import { EditOptions } from './edit-options';
import { MarkerRenderService } from './marker-render.service';
import { NameLabelsService } from './name-labels.service';
import { IBase, IExportStopRow, IMapConfig, IServiceArea, IStopDeletionWarning, IStopUsage } from 'src/app/models';
import { CreationSegmentsType, FeatureProperty, FeatureType, LegendType, StopType, StopMarkerStatus, MapTypes } from 'src/app/models/enums';
import { BehaviorSubject, Subject } from 'rxjs';
import { UtilsService } from '../utils/utils.service';
import { APP } from 'src/app/config/app';
import { images } from 'src/images';
import { IFlightPathSnapshot } from 'src/app/models/mapSnapshot';
import { LiteralService } from '../literal/literal.service';
import { TownsService } from '../towns/towns.service';
import { IRouteGraphRequest } from './route-graph.compute';

@Injectable({
  providedIn: 'root'
})
export class EventsOptions {

  public map: google.maps.Map;

  private selectedFeatureType: FeatureType = FeatureType.STOP;
  private selectedSegments: google.maps.Data.Feature[] = [];
  private selectedStops: google.maps.Data.Feature[] = [];
  private highlightedStops: {selectedStatus: StopMarkerStatus, ids: number[], status: StopMarkerStatus} = {selectedStatus: StopMarkerStatus.SELECTED, ids: [], status: StopMarkerStatus.SELECTED};
  private isHovering: number | null = null;

  public onSelectSegments = new BehaviorSubject<google.maps.Data.Feature[]>([]);
  public currentSegments$ = this.onSelectSegments.asObservable();
  public onSelectStops = new BehaviorSubject<google.maps.Data.Feature[]>([]);
  public currentStops$ = this.onSelectStops.asObservable();

  public onUpdateSegment = new BehaviorSubject<google.maps.Data.Feature[] | null>(null);
  public updatedSegment$ = this.onUpdateSegment.asObservable();
  public onUpdateStop = new BehaviorSubject<google.maps.Data.Feature[] | null>(null);
  public updatedStop$ = this.onUpdateStop.asObservable();
  public onStopDragged = new Subject<{ id: number, name: string }>();
  public stopDragged$ = this.onStopDragged.asObservable();

  public onMapClick = new BehaviorSubject<any>(null);
  public mapClicked$ = this.onMapClick.asObservable();

  public onDeleteFeature = new Subject<void>();
  public deletedFeature$ = this.onDeleteFeature.asObservable();

  public onAddStop = new Subject<void>();
  public addedStop$ = this.onAddStop.asObservable();

  public onDataLayerAttached = new Subject<void>();
  public dataLayerAttached$ = this.onDataLayerAttached.asObservable();
  public isDataLayerAttached = false;

  private newStopCounter: number = 0;

  public onStopDeletionWarning = new Subject<IStopDeletionWarning>();
  public stopDeletionWarning$ = this.onStopDeletionWarning.asObservable();

  public isEditableStop(id: number): boolean {
    return this.stops.has(id) || this.townStops.has(id);
  }

  public isTownStop(id: number): boolean {
    return this.townStops.has(id);
  }

  // Quick creation and alterations only ever add town stops: the backend ignores any change to the
  // ones already in the catalogue, so their shared fields stay read-only instead of silently reverting
  public setTownStopsCreateOnly(createOnly: boolean): void {
    this.townStopsCreateOnly = createOnly;
  }

  public canEditSharedStopFields(id: number): boolean {
    return !this.townStopsCreateOnly || !this.townCatalogueStopIds.has(id);
  }

  private originStop: any = null;
  private destinationStop: any = null;

  private startStop: any | null = null;
  private endStop: any | null = null;

  private stops = new Map<number, any>();
  private stopsMarkers = new Map<number, google.maps.marker.AdvancedMarkerElement>();
  private stopCircles = new Map<number, google.maps.Circle>();
  private segments = new Map<number, any>();
  private outlineDeleteIcon: google.maps.marker.AdvancedMarkerElement | null = null;
  private outlineDeleteIconRequest = 0;
  private selectedAreas: google.maps.Data.Feature[] = [];
  private targetAreaFeature: google.maps.Data.Feature | null = null;
  public areaInMenu = false;
  public areasEditable = true;
  private newAreaCounter = 0;
  private townStops = new Map<number, any>();
  private townStopsMarkers = new Map<number, google.maps.marker.AdvancedMarkerElement>();
  private editContext: { townId: number, routeId?: number } = { townId: 0 };
  private townCatalogueStopIds = new Set<number>();
  private townStopsCreateOnly = false;
  public townCatalogueLoaded = false;
  private townStopsLoading: Promise<void> = Promise.resolve();

  public markTownCatalogueLoaded(): void {
    this.townCatalogueLoaded = true;
  }
  private sessionStopIds = new Set<number>();

  public creationSegments = false;
  public creationSegmentsType = CreationSegmentsType.AUTO;
  public activeCursor = 'default';
  public onCreationSegments = new BehaviorSubject<boolean>(false);
  public currentCreationSegments$ = this.onCreationSegments.asObservable();

  public onFlightPathChange = new Subject<void>();
  public flightPathChange$ = this.onFlightPathChange.asObservable();

  public creationOutline = false;
  public onCreationOutline = new BehaviorSubject<boolean>(false);
  public currentCreationOutline$ = this.onCreationOutline.asObservable();
  public onSelectAreas = new BehaviorSubject<google.maps.Data.Feature[]>([]);
  public currentAreas$ = this.onSelectAreas.asObservable();
  private readonly defaultAreaStyles = { strokeWeight: 2, fillOpacity: 0.1 };
  private readonly selectedAreaStyles = { strokeWeight: 4, fillOpacity: 0.25 };
  public onUpdateAreas = new Subject<IServiceArea[]>();
  public updatedAreas$ = this.onUpdateAreas.asObservable();
  public outlineColor: string | undefined = undefined;
  public serviceColor: string | undefined = undefined;

  public somePoints: {latitude: number, longitude: number}[] = [];
  public coordinates: any[] = [];
  private drawnSegments: google.maps.Polyline[] = [];
  private ghostPolylines = new Map<number, google.maps.Polyline>();

  private listeners: {event: string, listener: google.maps.MapsEventListener}[] = [];
  private markerDomListeners = new Map<number, { element: any, event: string, handler: any }[]>();
  private editPolylineListeners: google.maps.MapsEventListener[] = [];
  public editingWaypointsSegmentId: number | null = null;

  private shiftPressed = false;

  private isBoxSelecting = false;
  private boxSelectOrigin: google.maps.LatLng | null = null;
  private selectionRectangle: google.maps.Rectangle | null = null;

  public menuBox: HTMLElement | null;
  public menuItemAddStop: any;
  public addStopHandler: any;

  public menuSegmentsBox: HTMLElement | null;
  public isRedrawingSegment = false;

  public menuStopsBox: HTMLElement | null;
  public menuAlternativeBox: HTMLElement | null;
  public menuBoxSelectBox: HTMLElement | null;
  private removeAlternativeHandler: any;
  public isSettingMergeStop = false;
  public isSettingAlternativeStop = false;

  public isShowingSpecificStopType: StopType | null = null;
  public originalSelectedStopId: number | null = null;
  public legendType: LegendType | undefined = undefined;

  defaultMapConfig: IMapConfig = {
    mapView: MapTypes.DEFAULT,
    showAllStops: true,
    showRouteStops: true,
    showStartStop: false,
    showEndStop: false,
    showSegments: true,
    showNames: false,
    showRadius: false,
    radius: APP.DEFAULT_STOP_RADIUS,
    drivingSpeed: undefined,
    creationSegmentsType: CreationSegmentsType.AUTO
  };
  public mapConfig: IMapConfig = { ...this.defaultMapConfig };

  public onMapConfigChange = new BehaviorSubject<IMapConfig>(this.mapConfig);
  public mapConfigChange$ = this.onMapConfigChange.asObservable();

  public selectedSegmentStyles = {
    strokeColor: '#8136db',
    strokeWeight: 6,
    zIndex: 2000
  };
  public defaultSegmentStyles = {
    strokeColor: '#280a54',
    strokeWeight: 5,
    zIndex: 5
  };
  public hoverSegmentStyles = {
    strokeColor: '#BD95EC',
    strokeWeight: 5,
    zIndex: 2000
  };

  constructor(
    private ngZone: NgZone,
    private deleteOptions: DeleteOptions,
    private createOptions: CreateOptions,
    private editOptions: EditOptions,
    private markerRenderService: MarkerRenderService,
    private utilsService: UtilsService,
    private nameLabelsService: NameLabelsService,
    private literalService: LiteralService,
    private townService: TownsService
  ) {
    this.loadMapConfig();
    this.creationSegmentsType = this.mapConfig.creationSegmentsType ?? CreationSegmentsType.AUTO;
    this.updatedAreas$.subscribe((areas) => this.syncOutlineDeleteIcon(areas));
  }

  public getStops() {
    const townStops = [...this.townStops.values()].filter((stop) => this.isTownStopVisible(stop.id));
    return [...this.stops.values(), ...townStops].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  // Looked up on demand so the user can fix the other route in another tab and retry without reloading this one
  public async getRoutesUsingStop(id: number): Promise<IBase[]> {
    if (!(this.editContext.townId > 0)) return [];
    return this.townService.getRoutesWithStop(this.editContext.townId, id, this.editContext.routeId);
  }

  public getStopName(id: number): string | undefined {
    return (this.stops.get(id) ?? this.townStops.get(id))?.name;
  }


  public getSegments() {
    return Array.from(this.segments.values()).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  public buildRouteGraphRequest(): IRouteGraphRequest | null {
    const stops = this.getStops().filter((stop: any) => stop.type === FeatureType.STOP);
    const startStop: any = stops.find((stop: any) => stop.start);
    const endStop: any = stops.find((stop: any) => stop.end);
    if (!startStop) return null;
    const segments = this.getSegments()
      .map((segment: any) => ({
        startStopId: segment.start?.properties?.id,
        endStopId: segment.end?.properties?.id,
        weightSeconds: Number(segment.weight ?? segment.distance ?? 0),
      }))
      .filter((segment: any) => segment.startStopId != null && segment.endStopId != null);
    return { startStopId: startStop.id, endStopId: endStop?.id ?? null, segments };
  }

  public getSelectedStops() {
    return this.selectedStops;
  }

  public setSelectedStop(event: {ids: number[], multiple: boolean}, updateMarker: boolean = true) {
    const { ids, multiple } = event;
    if (!multiple || ids.length === 0) {
      if (this.editingWaypointsSegmentId !== null) this.clearSelectedSegments();
      this.selectedStops.forEach((stop) => {
        this.updateMarkerIcon(Number(stop.getProperty('id')), StopMarkerStatus.DEFAULT);
      });
      this.selectedStops = [];
      this.showRelatedStopsOfType(null);
    } else {
      this.selectedStops = this.selectedStops.filter((stop) => ids.includes(Number(stop.getProperty('id'))));
    }
    this.map.data.forEach((feature) => {
      const featureType = feature.getProperty('type');
      if (featureType === FeatureType.STOP || featureType === FeatureType.TOWN_STOP) {
        if (ids.includes(Number(feature.getProperty('id')))) {
          if (!this.selectedStops.some((stop) => stop.getProperty('id') === feature.getProperty('id'))) this.selectedStops.push(feature);
          if (updateMarker) {
            this.updateMarkerIcon(Number(feature.getProperty('id')), StopMarkerStatus.SELECTED);
          }
        } else if (!this.highlightedStops.ids.includes(Number(feature.getProperty('id')))) {
          this.updateMarkerIcon(Number(feature.getProperty('id')), StopMarkerStatus.DEFAULT);
        }
      }
    });
    this.onSelectStops.next(this.selectedStops);
  }

  public clearSelectedStops() {
    if (this.selectedStops.length > 0) {
      this.isShowingSpecificStopType = null;
      this.originalSelectedStopId = null;
      this.selectedStops.forEach((stop) => {
        this.updateMarkerIcon(Number(stop.getProperty('id')), StopMarkerStatus.DEFAULT);
      });
      this.townStops.forEach((stop) => {
        this.updateMarkerIcon(stop.id, StopMarkerStatus.DEFAULT);
      });
      this.selectedStops = [];
    }
    this.clearRelatedStops();
    this.onSelectStops.next([]);
  }

  public getSelectedSegments() {
    return this.selectedSegments;
  }

  public setSelectedSegment(event: {ids: number[], multiple: boolean}) {
    const { ids, multiple } = event;
    if (!multiple || ids.length === 0) {
      this.selectedSegments.forEach((segment) => {
        this.map.data.overrideStyle(segment, this.defaultSegmentStyles);
      });
      this.selectedSegments = [];
    } else {
      const removed = this.selectedSegments.filter((segment) => !ids.includes(Number(segment.getProperty('id'))));
      removed.forEach((segment) => this.map.data.overrideStyle(segment, this.defaultSegmentStyles));
      this.selectedSegments = this.selectedSegments.filter((segment) => ids.includes(Number(segment.getProperty('id'))));
    }
    ids.forEach((id) => {
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, id, FeatureType.SEGMENT);
      if (feature) {
        if (!this.selectedSegments.some((segment) => segment.getProperty('id') === feature.getProperty('id'))) this.selectedSegments.push(feature);
        this.map.data.overrideStyle(feature, this.selectedSegmentStyles);
      }
    });
    this.onSelectSegments.next(this.selectedSegments);
  }

  public clearSelectedSegments() {
    if (this.editingWaypointsSegmentId) {
      this.editingWaypointsSegmentId = null;
      this.editPolylineListeners.forEach((l) => l.remove());
      this.editPolylineListeners = [];
      this.ghostPolylines.forEach((p) => p.setOptions({ editable: false }));
    }
    if (this.selectedSegments.length > 0) {
      this.selectedSegments.forEach((segment) => {
        this.map.data.overrideStyle(segment, this.defaultSegmentStyles);
      });
      this.selectedSegments = [];
    }
    this.onSelectSegments.next([]);
  }

  public hoverFeatureFromPanel(id: number, type: FeatureType) {
    if (this.isVisuallySelectedFeature(id, type)) return;
    if (type === FeatureType.SEGMENT) {
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, id, FeatureType.SEGMENT);
      if (feature) this.map.data.overrideStyle(feature, this.hoverSegmentStyles);
    } else {
      this.updateMarkerIcon(id, StopMarkerStatus.HOVER);
    }
  }

  public clearHoverFeatureFromPanel(id: number, type: FeatureType) {
    if (this.isVisuallySelectedFeature(id, type)) return;
    if (type === FeatureType.SEGMENT) {
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, id, FeatureType.SEGMENT);
      if (feature) this.map.data.overrideStyle(feature, this.defaultSegmentStyles);
    } else {
      this.updateMarkerIcon(id, StopMarkerStatus.DEFAULT);
    }
  }

  // Connections are the opposite type to the active panel; only guard when the hovered feature is the visually-selected type.
  private isVisuallySelectedFeature(id: number, type: FeatureType): boolean {
    if (this.selectedFeatureType !== type) return false;
    if (type === FeatureType.SEGMENT) {
      return this.selectedSegments.some((s) => Number(s.getProperty('id')) === id);
    }
    return this.selectedStops.some((s) => Number(s.getProperty('id')) === id)
      || this.highlightedStops.ids.includes(id);
  }

  // MAP VIEW OPTIONS
  public showOnlySelectedType(type: FeatureType) {
    this.selectedFeatureType = type;
    if (type === FeatureType.STOP) {
      this.selectedSegments.forEach((segment) => {
        this.map.data.overrideStyle(segment, this.defaultSegmentStyles);
      });
      this.selectedStops.forEach((stop) => {
        this.updateMarkerIcon(Number(stop.getProperty('id')), StopMarkerStatus.SELECTED);
      });
    } else {
      this.showRelatedStopsOfType(null);
      this.selectedStops.forEach((stop) => {
        this.updateMarkerIcon(Number(stop.getProperty('id')), StopMarkerStatus.DEFAULT);
      });
      this.selectedSegments.forEach((segment) => {
        this.map.data.overrideStyle(segment, this.selectedSegmentStyles);
      });
    }
  }

  public selectAllStopsOfType(type: StopType) {
    this.clearSelectedStops();
    this.isShowingSpecificStopType = type;
    const allStops = Array.from(this.stops.values());
    let stops = allStops.filter((stop) => stop[type]);
    if (type === StopType.ALTERNATIVES) {
      let alternativeStops: any[] = [];
      stops.forEach((stop) => {
        stop.alternatives?.forEach((alternative: any) => {
          if (!alternativeStops.some((s) => s.id === alternative.properties.id)) {
            alternativeStops.push(this.stops.get(alternative.properties.id));
          }
        });
      });
      stops = alternativeStops;
    }
    this.setSelectedStop({ids: stops.map((stop) => stop.id), multiple: true}, false);
    const demandStops = allStops.filter((stop) => stop.demand);
    demandStops.forEach((stop) => {
      this.updateMarkerIcon(stop.id, StopMarkerStatus.DEMAND);
    });
    stops.forEach((stop) => {
      this.updateMarkerIcon(stop.id, StopMarkerStatus[`${type.toUpperCase()}_SELECTED` as keyof typeof StopMarkerStatus]);
    });
  }

  private checkIfSelectedStopsAreAlternativesOf(stopId: number) {
    return this.selectedStops.every((stop) => {
      const alternatives: any = stop.getProperty('alternatives') || [];
      return alternatives.some((alternative: any) => alternative.properties.id === stopId);
    });
  }

  public showRelatedStopsOfType(type: StopType | null) {
    this.isShowingSpecificStopType = type;
    this.clearRelatedStops();
    if (this.selectedStops.length === 0) return;
    const selectedStopId = Number(this.selectedStops[0].getProperty('id'));
    const selectedStop = this.stops.get(selectedStopId) ?? this.townStops.get(selectedStopId);
    if (!selectedStop) return;
    const highlightedIds: number[] = [];
    let selectedStopStatus: StopMarkerStatus | null = null;
    this.highlightedStops.selectedStatus = StopMarkerStatus.SELECTED;

    if (this.originalSelectedStopId) {
      if (this.checkIfSelectedStopsAreAlternativesOf(this.originalSelectedStopId)) {
        this.selectedStops = [this.utilsService.findFeatureByPropertyId(this.map.data, this.originalSelectedStopId, FeatureType.STOP)!];
        selectedStopStatus = StopMarkerStatus.SELECTED;
        this.onSelectStops.next(this.selectedStops);
      }
      this.originalSelectedStopId = null;
    }

    switch (type) {
      case StopType.ALTERNATIVES:
        selectedStop.alternatives?.forEach((a: any) =>
          highlightedIds.push(a.properties.id)
        );
        this.highlightedStops.selectedStatus = StopMarkerStatus.SELECTED;
        this.highlightedStops.status = StopMarkerStatus.ALTERNATIVES_SELECTED;
        selectedStopStatus = StopMarkerStatus.SELECTED;
        break;
      case StopType.ALTERNATIVE_OF:
        Array.from(this.stops.values())
          .filter((s) => s.alternatives?.some((a: any) => a.properties.id === selectedStop.id))
          .forEach((s) => highlightedIds.push(s.id));
        this.highlightedStops.selectedStatus = StopMarkerStatus.ALTERNATIVES_SELECTED;
        this.highlightedStops.status = StopMarkerStatus.SELECTED;
        selectedStopStatus = StopMarkerStatus.ALTERNATIVES_SELECTED;
        this.originalSelectedStopId = selectedStop.id;
        break;
      case StopType.INFLEXION:
        if (selectedStop.inflexion) selectedStopStatus = StopMarkerStatus.INFLEXION_SELECTED;
        break;
      case StopType.END:
        if (selectedStop.end) selectedStopStatus = StopMarkerStatus.END_SELECTED;
        break;
      case StopType.START:
        if (selectedStop.start) selectedStopStatus = StopMarkerStatus.START_SELECTED;
        break;
      case StopType.MERGE:
        if (selectedStop.bidirectional) {
          const bidirectionalStopId = selectedStop.bidirectional.properties.id;
          const mergedStop = this.stops.get(bidirectionalStopId) ?? this.townStops.get(bidirectionalStopId);
          if (mergedStop) highlightedIds.push(mergedStop.id);
        }
        this.highlightedStops.status = StopMarkerStatus.MERGE_SELECTED;
        this.originalSelectedStopId = selectedStop.id;
        break;
    }
    
    highlightedIds.forEach((id) => {
      this.updateMarkerIcon(id, this.highlightedStops.status);
    });
    this.highlightedStops.ids = highlightedIds;

    if (selectedStopStatus) {
      this.updateMarkerIcon(selectedStop.id, selectedStopStatus);
    }
  }

  public clearRelatedStops() {
    this.highlightedStops.ids.forEach((id) => {
      this.updateMarkerIcon(id, StopMarkerStatus.DEFAULT);
    });
    this.highlightedStops = { selectedStatus: StopMarkerStatus.SELECTED, ids: [], status: StopMarkerStatus.DEFAULT };
  }

  public clearSelectedStopType() {
    this.isShowingSpecificStopType = null;
    this.clearSelectedStops();
    this.stops.forEach((stop) => {
      this.updateMarkerIcon(stop.id, StopMarkerStatus.DEFAULT);
    });
  }

  public changeCreationSegments(creationSegments: boolean, type?: CreationSegmentsType) {
    this.creationSegments = creationSegments;
    if (type) {
      this.creationSegmentsType = type;
      this.mapConfig.creationSegmentsType = type;
      if (type === CreationSegmentsType.AUTO) {
        this.mapConfig.drivingSpeed = undefined;
        this.activeCursor = this.buildCursor(images.cursor.creationSegmentsAuto);
      } else {
        const stored = localStorage.getItem(APP.LOCAL_STORAGE_MAP_CONFIG);
        const savedSpeed = stored ? JSON.parse(stored)?.drivingSpeed : undefined;
        this.mapConfig.drivingSpeed = savedSpeed ?? APP.DEFAULT_DRIVING_SPEED;
        this.activeCursor = this.buildCursor(images.cursor.creationSegmentsManual);
      }
      localStorage.setItem(APP.LOCAL_STORAGE_MAP_CONFIG, JSON.stringify(this.mapConfig));
    }
    if (creationSegments) {
      this.setStopMarkersDraggable(false);
      this.ghostPolylines.forEach((p) => p.setOptions({ clickable: false }));
    } else {
      this.startStop = null;
      if (!this.shiftPressed) this.setStopMarkersDraggable(true);
      this.activeCursor = this.buildCursor(images.cursor.default);
      this.ghostPolylines.forEach((p) => p.setOptions({ clickable: true }));
    }
    this.clearSelectedStops();
    this.clearSelectedSegments();
    this.clearFlightPath();
    this.map.setOptions({ draggableCursor: this.activeCursor });
    this.forceCursor();
    this.onCreationSegments.next(creationSegments);
  }

  public changeCreationOutline(creationOutline: boolean, color?: string) {
    this.creationOutline = creationOutline;
    if (color) this.outlineColor = color;
    this.clearSelectedStops();
    this.clearSelectedSegments();
    this.clearFlightPath();
    this.clearSelectedAreas();
    this.removeOutlineDeleteIcon();
    if (creationOutline) this.map.data.toGeoJson((geoJson: any) => this.syncOutlineDeleteIcon(this.utilsService.featuresToAreas(geoJson.features)));
    this.ghostPolylines.forEach((p) => p.setOptions({ clickable: !creationOutline }));
    this.activeCursor = creationOutline ? 'crosshair' : this.buildCursor(images.cursor.default);
    this.map.setOptions({ draggableCursor: this.activeCursor });
    this.forceCursor();
    this.onCreationOutline.next(creationOutline);
  }

  private removeOutlineDeleteIcon(): void {
    this.outlineDeleteIconRequest++;
    if (this.outlineDeleteIcon) {
      this.outlineDeleteIcon.map = null;
      this.outlineDeleteIcon = null;
    }
  }

  private syncOutlineDeleteIcon(areas: IServiceArea[]): void {
    this.removeOutlineDeleteIcon();
    if (this.creationOutline && areas.length === 1) this.addOutlineDeleteIcon(areas[0]);
  }

  private async addOutlineDeleteIcon(area: IServiceArea): Promise<void> {
    const request = this.outlineDeleteIconRequest;
    const position = this.createOptions.utilsGeo.getOutlineTopRightPoint(area.perimeter.coordinates);
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    if (request !== this.outlineDeleteIconRequest) return;
    const color = this.outlineColor ?? '#280a54';
    const iconContainer = document.createElement('div');
    Object.assign(iconContainer.style, {
      backgroundColor: 'white', position: 'absolute', top: '-12px', left: '-12px',
      borderRadius: '50%', width: '32px', height: '32px', border: `2px solid ${color}`,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    });
    const iconDiv = document.createElement('img');
    Object.assign(iconDiv.style as any, {
      width: '24px', height: '24px', maskSize: 'cover', webkitMaskSize: 'cover',
      maskImage: `url(${images.close})`, webkitMaskImage: `url(${images.close})`,
      backgroundColor: color,
    });
    iconContainer.appendChild(iconDiv);
    this.outlineDeleteIcon = new AdvancedMarkerElement({ map: this.map, position, content: iconContainer });
    this.outlineDeleteIcon.addListener('gmp-click', () => {
      this.getAreaFeatures().forEach((feature) => this.map.data.remove(feature));
      this.emitAreas();
    });
  }

  private canEditAreas(): boolean {
    return this.areasEditable && this.getAreaFeatures().length > 1;
  }

  private getAreaFeatures(): google.maps.Data.Feature[] {
    const areaFeatures: google.maps.Data.Feature[] = [];
    this.map.data.forEach((feature) => {
      if (feature.getProperty(FeatureProperty.TYPE) === FeatureType.OUTLINE) areaFeatures.push(feature);
    });
    return areaFeatures;
  }

  public getAreas(): { id: string; name: string }[] {
    return this.getAreaFeatures().map((feature) => ({
      id: this.areaIdOf(feature),
      name: String(feature.getProperty(FeatureProperty.NAME) ?? ''),
    }));
  }

  private areaIdOf(feature: google.maps.Data.Feature): string {
    return String(feature.getProperty(FeatureProperty.ID));
  }

  public getSelectedAreas(): google.maps.Data.Feature[] {
    return this.selectedAreas;
  }

  public setSelectedArea(event: { ids: string[] }): void {
    this.selectedAreas.forEach((feature) => this.map.data.overrideStyle(feature, this.defaultAreaStyles));
    this.selectedAreas = this.getAreaFeatures().filter((feature) => event.ids.includes(this.areaIdOf(feature)));
    this.selectedAreas.forEach((feature) => this.map.data.overrideStyle(feature, this.selectedAreaStyles));
    this.onSelectAreas.next(this.selectedAreas);
  }

  public clearSelectedAreas(): void {
    if (this.selectedAreas.length > 0) this.setSelectedArea({ ids: [] });
  }

  private selectArea(feature: google.maps.Data.Feature): void {
    this.hideContextMenus();
    this.cancelOnGoingStopAction();
    this.clearSelectedStops();
    this.clearSelectedSegments();
    this.setSelectedArea({ ids: [this.areaIdOf(feature)] });
  }

  public renameArea(id: string, name: string): void {
    const areaFeatures = this.getAreaFeatures();
    const feature = areaFeatures.find((areaFeature) => this.areaIdOf(areaFeature) === id);
    if (!feature) return;
    feature.setProperty(FeatureProperty.NAME, name.trim() || this.buildAreaName(areaFeatures.indexOf(feature)));
    this.emitAreas();
  }

  private buildAreaName(index: number): string {
    return `${this.literalService.get('services.routes.design.area.title', true)} ${index + 1}`;
  }

  private emitAreas(): void {
    this.map.data.toGeoJson((geoJson: any) => this.onUpdateAreas.next(this.utilsService.featuresToAreas(geoJson.features)));
  }

  public attachDataLayerEvents(map: google.maps.Map, townId: number, routeId?: number): void {
    if (map) this.map = map;
    this.editContext = { townId, routeId };
    document.addEventListener('keydown', this.keydownHandler);
    document.addEventListener('keyup', this.keyupHandler);
    window.addEventListener('blur', this.windowBlurHandler);

    const onClickListener = this.buildMapClickListener();
    const onRightClickListener = this.buildMapRightClickListener();
    this.newStopCounter = 0;

    this.listeners.push({ event: 'click', listener: this.map.addListener('click', onClickListener) });
    this.listeners.push({
      event: 'click',
      listener: this.map.data.addListener('click', (event: any) => {
        const type = event.feature.getProperty('type');
        this.ngZone.run(() => {
          if (type === FeatureType.SEGMENT) {
            if (this.creationSegments || this.creationOutline) onClickListener(event);
            else this.onSegmentClick(event.feature);
          }
          if (type === FeatureType.OUTLINE) {
            if (this.creationSegments || this.creationOutline || this.shiftPressed || !this.canEditAreas()) onClickListener(event);
            else this.selectArea(event.feature);
          }
        });
      })
    });
    this.listeners.push({
      event: 'rightclick',
      listener: this.map.addListener('rightclick', (e: any) => this.ngZone.run(() => {
        this.targetAreaFeature = null;
        this.areaInMenu = false;
        onRightClickListener(e);
      })),
    });
    this.listeners.push({
      event: 'rightclick-dataLayer',
      listener: this.map.data.addListener('rightclick', (e: any) => {
        const type = e.feature.getProperty('type');
        if (type === FeatureType.SEGMENT) {
          if (this.creationSegments || this.creationOutline) return;
          this.clearSelectedSegments();
          this.setSelectedSegment({ ids: [e.feature.getProperty('id')], multiple: false });
          const mouseEvt = e.domEvent as MouseEvent;
          this.menuSegmentsBox = document.getElementById("menu-segments");
          this.menuSegmentsBox!.style.display = "block";
          this.utilsService.positionMenuWithinViewport(this.menuSegmentsBox!, mouseEvt.clientX, mouseEvt.clientY);
          mouseEvt.preventDefault();
        }
        if (type === FeatureType.OUTLINE) {
          const showAreaActions = this.canEditAreas();
          if (showAreaActions && (this.creationSegments || this.creationOutline)) return;
          this.ngZone.run(() => {
            this.targetAreaFeature = showAreaActions ? e.feature : null;
            this.areaInMenu = showAreaActions;
            onRightClickListener(e);
          });
        }
      })
    });
    this.listeners.push({ event: 'dragstart', listener: this.map.addListener('dragstart', () => this.hideContextMenus()) });
    this.listeners.push({ event: 'mousedown', listener: this.map.addListener('mousedown', (e: any) => this.onBoxSelectMouseDown(e)) });
    this.listeners.push({ event: 'mousemove', listener: this.map.addListener('mousemove', (e: any) => this.onBoxSelectMouseMove(e)) });
    this.listeners.push({ event: 'mouseup', listener: this.map.addListener('mouseup', (e: any) => this.onBoxSelectMouseUp(e)) });
    this.listeners.push({
      event: 'mouseover',
      listener: this.map.data.addListener('mouseover', (event: any) => {
        if (this.isBoxSelecting) return;
        if (this.creationSegments || this.creationOutline || this.isSettingMergeStop || this.isSettingAlternativeStop || this.shiftPressed) {
          this.map.data.overrideStyle(event.feature, { cursor: this.activeCursor });
          return;
        }
        if (event.feature.getProperty('type') === FeatureType.SEGMENT && this.selectedSegments.every((s) => s !== event.feature)) {
          this.map.data.overrideStyle(event.feature, this.hoverSegmentStyles);
          this.activeCursor = this.buildCursor(images.cursor.hover);
        }
        this.map.data.overrideStyle(event.feature, { cursor: this.activeCursor });
      })
    });
    this.listeners.push({
      event: 'mouseout',
      listener: this.map.data.addListener('mouseout', (event: any) => {
        if (this.creationSegments || this.creationOutline || this.isSettingMergeStop || this.isSettingAlternativeStop || this.shiftPressed) return;
        if (event.feature.getProperty('type') === FeatureType.SEGMENT && this.selectedSegments.every((s) => s !== event.feature)) {
          this.map.data.overrideStyle(event.feature, this.defaultSegmentStyles);
          this.activeCursor = this.buildCursor(images.cursor.default);
        }
        this.map.data.overrideStyle(event.feature, { cursor: this.activeCursor });
      })
    });
    this.listeners.push({
      event: 'addfeature',
      listener: this.map.data.addListener('addfeature', (event: any) => {
        if (event.feature.getProperty('type') === FeatureType.SEGMENT) this.addGhostPolyline(event.feature);
      })
    });
    this.listeners.push({
      event: 'removefeature',
      listener: this.map.data.addListener('removefeature', (event: any) => {
        if (event.feature.getProperty('type') === FeatureType.SEGMENT) this.removeGhostPolyline(event.feature.getProperty('id'));
      })
    });
    this.map.data.forEach((feature: any) => {
      if (feature.getProperty('type') === FeatureType.SEGMENT) this.addGhostPolyline(feature);
    });
    this.setupMarkerListeners();
    this.sessionStopIds.clear();
    this.seedMinId(townId);
    this.isDataLayerAttached = true;
    this.onDataLayerAttached.next();
  }

  // The town answers after features may already have been drawn, so the counter is only ever lowered:
  // raising it would hand out again ids still on the map, and an added feature replaces the one with its id
  private async seedMinId(townId: number) {
    const minId = townId > 0 ? await this.townService.getLatestFeatureId(townId) : 0;
    this.createOptions.setMinId(Math.min(minId, this.createOptions.minId));
  }

  private addGhostPolyline(feature: google.maps.Data.Feature): void {
    const id = feature.getProperty('id') as number;
    if (this.ghostPolylines.has(id)) return;
    const geometry = feature.getGeometry();
    if (!geometry || geometry.getType() !== 'LineString') return;
    const path = (geometry as google.maps.Data.LineString).getArray();
    const ghost = new google.maps.Polyline({ path, map: this.map, strokeOpacity: 0, strokeWeight: 20, clickable: true, zIndex: 20, suppressUndo: true } as any);
    ghost.addListener('mouseover', () => {
      if (this.creationSegments || this.creationOutline || this.isSettingMergeStop || this.isSettingAlternativeStop || this.shiftPressed) return;
      if (this.selectedSegments.every((s) => s !== feature)) {
        this.map.data.overrideStyle(feature, this.hoverSegmentStyles);
        this.activeCursor = this.buildCursor(images.cursor.hover);
        this.map.setOptions({ draggableCursor: this.activeCursor });
        (ghost as any).setOptions({ cursor: this.activeCursor });
      }
    });
    ghost.addListener('mouseout', () => {
      if (this.creationSegments || this.creationOutline || this.isSettingMergeStop || this.isSettingAlternativeStop || this.shiftPressed) return;
      if (this.selectedSegments.every((s) => s !== feature)) {
        this.map.data.overrideStyle(feature, this.defaultSegmentStyles);
        this.activeCursor = this.buildCursor(images.cursor.default);
        this.map.setOptions({ draggableCursor: this.activeCursor });
        (ghost as any).setOptions({ cursor: this.activeCursor });
      }
    });
    ghost.addListener('click', () => {
      this.ngZone.run(() => {
        if (!this.creationSegments && !this.creationOutline) this.onSegmentClick(feature);
      });
    });
    ghost.addListener('rightclick', (e: any) => {
      this.ngZone.run(() => {
        this.clearSelectedSegments();
        this.setSelectedSegment({ ids: [id], multiple: false });
        const mouseEvt = e.domEvent as MouseEvent;
        this.menuSegmentsBox = document.getElementById('menu-segments');
        if (!this.menuSegmentsBox) return;
        this.menuSegmentsBox.style.display = 'block';
        this.utilsService.positionMenuWithinViewport(this.menuSegmentsBox, mouseEvt.clientX, mouseEvt.clientY);
        mouseEvt.preventDefault();
      });
    });
    this.ghostPolylines.set(id, ghost);
  }

  private removeGhostPolyline(id: number): void {
    const ghost = this.ghostPolylines.get(id);
    if (!ghost) return;
    ghost.setMap(null);
    google.maps.event.clearInstanceListeners(ghost);
    this.ghostPolylines.delete(id);
  }

  private syncGhostPolylinesForStop(stopId: number): void {
    this.segments.forEach((segment, segId) => {
      if (segment.start?.properties?.id !== stopId && segment.end?.properties?.id !== stopId) return;
      const polyline = this.ghostPolylines.get(segId);
      if (!polyline) return;
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, segId, FeatureType.SEGMENT);
      if (!feature) return;
      polyline.setPath((feature.getGeometry() as google.maps.Data.LineString).getArray());
    });
  }

  private clearGhostPolylines(): void {
    this.ghostPolylines.forEach((_, id) => this.removeGhostPolyline(id));
    this.ghostPolylines.clear();
  }

  private buildMapClickListener(): (e: any) => void {
    return (e: any) => {
      this.hideContextMenus();
      this.cancelOnGoingStopAction();
      if (this.creationSegments) {
        if (this.startStop) {
          this.somePoints.push({ latitude: e.latLng.toJSON().lat, longitude: e.latLng.toJSON().lng });
          this.coordinates.push({ lat: e.latLng.toJSON().lat, lng: e.latLng.toJSON().lng });
          this.drawFlightPath(this.coordinates);
        }
      } else if (this.creationOutline) {
        const newPoint = { lat: e.latLng.toJSON().lat, lng: e.latLng.toJSON().lng };
        if (this.coordinates.length >= 3) {
          const firstPoint = { lat: this.coordinates[0].lat, lng: this.coordinates[0].lng };
          const distanceToFirst = this.createOptions.utilsGeo.calculateDistanceBetweenPoints(firstPoint, newPoint);
          const closingThreshold = this.createOptions.utilsGeo.getClosingThreshold(this.map.getZoom() ?? 14, firstPoint.lat);
          if (distanceToFirst <= closingThreshold) {
            this.newAreaCounter++;
            this.createOptions.buildAreaFeature(
              this.map,
              this.coordinates,
              `${FeatureType.OUTLINE}-new-${this.newAreaCounter}`,
              this.buildAreaName(this.getAreaFeatures().length),
              this.outlineColor,
            );
            this.changeCreationOutline(false);
            this.emitAreas();
            return;
          }
        }
        this.somePoints.push({ latitude: newPoint.lat, longitude: newPoint.lng });
        this.coordinates.push(newPoint);
        this.drawFlightPath(this.coordinates);
      } else {
        if (!this.shiftPressed) {
          if (this.selectedSegments.length > 0) this.clearSelectedSegments();
          if (this.selectedStops.length > 0) this.clearSelectedStops();
          this.clearSelectedAreas();
        }
      }
      this.onMapClick.next(null);
    };
  }

  private buildMapRightClickListener(): (e: any) => void {
    return (e: any) => {
      const mouseEvt = e.domEvent as MouseEvent;
      this.menuBox = document.getElementById("menu");
      this.menuBox!.style.display = "block";
      this.utilsService.positionMenuWithinViewport(this.menuBox!, mouseEvt.clientX, mouseEvt.clientY);
      this.menuItemAddStop = this.menuBox?.querySelector('#addStop');
      mouseEvt.preventDefault();
      this.removeAddStopHandlers();
      const coordinates = { lat: e.latLng.toJSON().lat, lng: e.latLng.toJSON().lng };
      this.addStopHandler = () => this.addStopFromMenu(coordinates, FeatureType.TOWN_STOP);
      this.menuItemAddStop.addEventListener("click", this.addStopHandler, { once: true });
    };
  }

  private removeAddStopHandlers(): void {
    if (this.addStopHandler) this.menuItemAddStop?.removeEventListener('click', this.addStopHandler);
  }

  // A new stop belongs to the town until it gets connected to the route (segment, start/end, inflexion or alternative)
  private async addStopFromMenu(coordinates: { lat: number, lng: number }, type: FeatureType): Promise<void> {
    const namePlaceholder = `${this.literalService.get('services.routes.design.fstop.title', true)} ${this.newStopCounter + 1}`;
    const pointFeature = this.createOptions.addStop(this.map, coordinates, this.mapConfig.radius, namePlaceholder, type);
    const stopId = Number(pointFeature.getProperty('id'));
    this.sessionStopIds.add(stopId);
    this.newStopCounter++;
    if (type === FeatureType.TOWN_STOP) {
      this.map.data.overrideStyle(pointFeature, { visible: false });
      const townStop = this.getStopProperties(stopId, pointFeature);
      this.townStops.set(stopId, townStop);
      await this.addMarker(townStop, true, true, this.townStopsMarkers);
      this.applyTownStopsVisibility();
      await this.renderStopNames();
    } else {
      await this.loadSegmentAndStops(true);
    }
    this.menuBox!.style.display = "";
    this.clearSelectedStops();
    this.selectedStops.push(pointFeature);
    this.updateMarkerIcon(stopId, StopMarkerStatus.SELECTED);
    this.onSelectStops.next(this.selectedStops);
    this.onAddStop.next();
  }

  private onBoxSelectMouseDown(e: any): void {
    if (!this.shiftPressed || !e.latLng || this.creationSegments || this.creationOutline || this.editingWaypointsSegmentId !== null) return;
    this.hideContextMenus();
    this.isBoxSelecting = true;
    this.boxSelectOrigin = e.latLng;
    this.map.setOptions({ draggable: false });
    this.selectionRectangle = new google.maps.Rectangle({
      map: this.map,
      bounds: new google.maps.LatLngBounds(e.latLng, e.latLng),
      clickable: false,
      strokeColor: '#18A0FB',
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillColor: '#18A0FB',
      fillOpacity: 0.20,
      zIndex: 3000
    });
  }

  private onBoxSelectMouseMove(e: any): void {
    if (!this.isBoxSelecting || !this.boxSelectOrigin || !e.latLng || !this.selectionRectangle) return;
    this.selectionRectangle.setBounds(new google.maps.LatLngBounds().extend(this.boxSelectOrigin).extend(e.latLng));
  }

  private onBoxSelectMouseUp(e: any): void {
    if (!this.isBoxSelecting) return;
    const bounds = this.selectionRectangle?.getBounds() ?? null;
    this.cancelBoxSelect();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const isNegligibleDrag = Math.abs(ne.lat() - sw.lat()) < 1e-7 && Math.abs(ne.lng() - sw.lng()) < 1e-7;
    if (isNegligibleDrag) return;

    this.ngZone.run(() => {
      const stopIds: number[] = [];
      const segmentIds: number[] = [];
      this.map.data.forEach((feature) => {
        const type = feature.getProperty('type');
        if (type === FeatureType.STOP || type === FeatureType.TOWN_STOP) {
          const geometry = feature.getGeometry() as any;
          const latLng = new google.maps.LatLng(geometry.elements.lat(), geometry.elements.lng());
          if (bounds.contains(latLng)) stopIds.push(Number(feature.getProperty('id')));
        } else if (type === FeatureType.SEGMENT) {
          const path = (feature.getGeometry() as google.maps.Data.LineString).getArray();
          if (path.length === 0) return;
          if (bounds.contains(path[0]) && bounds.contains(path[path.length - 1])) {
            segmentIds.push(Number(feature.getProperty('id')));
          }
        }
      });

      stopIds.forEach((id) => {
        if (this.townStops.has(id)) this.convertToNormalStop(id);
      });

      this.setSelectedStop({ ids: stopIds, multiple: true });
      this.setSelectedSegment({ ids: segmentIds, multiple: true });
      this.selectedStops.forEach((stop) => this.updateMarkerIcon(Number(stop.getProperty('id')), StopMarkerStatus.SELECTED));
      this.selectedSegments.forEach((segment) => this.map.data.overrideStyle(segment, this.selectedSegmentStyles));

      if (stopIds.length > 0 || segmentIds.length > 0) {
        this.showBoxSelectMenu(e.domEvent as MouseEvent, stopIds.length > 0, segmentIds.length > 0);
      }
    });
  }

  private cancelBoxSelect(): void {
    this.isBoxSelecting = false;
    this.boxSelectOrigin = null;
    this.map && this.map.setOptions({ draggable: true });
    this.selectionRectangle?.setMap(null);
    this.selectionRectangle = null;
  }

  private showBoxSelectMenu(mouseEvt: MouseEvent, hasStops: boolean, hasSegments: boolean): void {
    this.menuBoxSelectBox = document.getElementById('menu-box-select');
    if (!this.menuBoxSelectBox) return;
    this.menuBoxSelectBox.style.display = 'flex';
    this.utilsService.positionMenuWithinViewport(this.menuBoxSelectBox, mouseEvt.clientX, mouseEvt.clientY);
    const deleteStopsItem = this.menuBoxSelectBox.querySelector('#deleteBoxStops') as HTMLElement | null;
    const deleteSegmentsItem = this.menuBoxSelectBox.querySelector('#deleteBoxSegments') as HTMLElement | null;
    const deleteAllItem = this.menuBoxSelectBox.querySelector('#deleteBoxAll') as HTMLElement | null;
    if (deleteStopsItem) deleteStopsItem.style.display = hasStops ? '' : 'none';
    if (deleteSegmentsItem) deleteSegmentsItem.style.display = hasSegments ? '' : 'none';
    if (deleteAllItem) deleteAllItem.style.display = (hasStops && hasSegments) ? '' : 'none';
  }

  private setupMarkerListeners(): void {
    this.stopsMarkers.forEach((marker, markerId) => {
      marker.gmpDraggable = this.canEditSharedStopFields(markerId);
      marker.gmpClickable = true;
      const stop = this.stops.get(markerId);
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, markerId, FeatureType.STOP)!;
      this.attachMarkerEvents(markerId, marker, stop, feature);
    });
    this.townStopsMarkers.forEach((marker, markerId) => {
      marker.gmpDraggable = this.canEditSharedStopFields(markerId);
      marker.gmpClickable = true;
      const stop = this.townStops.get(markerId);
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, markerId, FeatureType.TOWN_STOP)!;
      this.attachMarkerEvents(markerId, marker, stop, feature);
    });
  }

  private setStopMarkersDraggable(draggable: boolean): void {
    this.stopsMarkers.forEach((marker, markerId) => { marker.gmpDraggable = draggable && this.canEditSharedStopFields(markerId); });
    this.townStopsMarkers.forEach((marker, markerId) => { marker.gmpDraggable = draggable && this.canEditSharedStopFields(markerId); });
  }

  hideContextMenus() {
    if (this.menuBox) {
      this.menuBox.style.display = "";
      this.removeAddStopHandlers();
    }
    if (this.menuSegmentsBox) this.menuSegmentsBox.style.display = "";
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
    if (this.menuAlternativeBox) this.menuAlternativeBox.style.display = "";
    if (this.menuBoxSelectBox) this.menuBoxSelectBox.style.display = "";
  }

  public targetMenuArea(): string | null {
    this.hideContextMenus();
    return this.targetAreaFeature ? this.areaIdOf(this.targetAreaFeature) : null;
  }

  public targetSelectedArea(): boolean {
    this.targetAreaFeature = this.selectedAreas[0] ?? null;
    return this.targetAreaFeature !== null;
  }

  public getTargetAreaName(): string {
    return String(this.targetAreaFeature?.getProperty(FeatureProperty.NAME) ?? '');
  }

  public deleteTargetArea(): void {
    if (!this.targetAreaFeature) return;
    const deletedAreaId = this.areaIdOf(this.targetAreaFeature);
    this.map.data.remove(this.targetAreaFeature);
    this.targetAreaFeature = null;
    const remainingSelectedIds = this.selectedAreas
      .map((feature) => this.areaIdOf(feature))
      .filter((id) => id !== deletedAreaId);
    this.selectedAreas = this.selectedAreas.filter((feature) => this.areaIdOf(feature) !== deletedAreaId);
    this.setSelectedArea({ ids: remainingSelectedIds });
    this.emitAreas();
  }

  private detachMarkerListeners(id: number, marker?: google.maps.marker.AdvancedMarkerElement) {
    const listeners = this.markerDomListeners.get(id) ?? [];
    if (marker && !listeners.some((listener) => listener.element === marker)) return;
    listeners.forEach((l) => l.element.removeEventListener(l.event, l.handler));
    this.markerDomListeners.delete(id);
  }

  private reattachMarkerEvents(id: number, stop: any): void {
    this.detachMarkerListeners(id);
    const isTownStop = !this.stops.has(id) && this.townStops.has(id);
    const marker = isTownStop ? this.townStopsMarkers.get(id) : this.stopsMarkers.get(id);
    if (!marker) return;
    const featureType = isTownStop ? FeatureType.TOWN_STOP : FeatureType.STOP;
    this.attachMarkerEvents(id, marker, stop, this.utilsService.findFeatureByPropertyId(this.map.data, id, featureType)!);
  }

  // SEGMENT ACTIONS
  redrawSegment() {
    this.deleteOptions.removeElement(this.map, this.selectedSegments[0].getProperty('id'), FeatureType.SEGMENT);
    this.hideContextMenus();
    this.isRedrawingSegment = true;
    this.changeCreationSegments(true, this.creationSegmentsType);
  }

  editWaypointsSegment() {
    this.hideContextMenus();
    const segment = this.selectedSegments[0];
    if (!segment) return;
    const id = segment.getProperty('id') as number;
    const polyline = this.ghostPolylines.get(id);
    if (!polyline) return;
    polyline.setOptions({ editable: true });
    this.editingWaypointsSegmentId = id;
    this.editPolylineListeners = this.editOptions.syncPolylineToSegment(this.map, polyline, id);
    const path = polyline.getPath();
    const emitUpdate = () => this.onUpdateSegment.next(this.selectedSegments);
    this.editPolylineListeners.push(
      path.addListener('set_at', emitUpdate),
      path.addListener('insert_at', emitUpdate),
      path.addListener('remove_at', emitUpdate),
    );
  }

  public async applyImportedStops(rows: IExportStopRow[]): Promise<void> {
    // Stops applied right after opening the map must find the town stops they coincide with, not duplicate them
    await this.townStopsLoading;
    const keyOf = (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`;
    const routeKeys = new Set(Array.from(this.stops.values()).map((s) => keyOf(s.lat, s.lng)));
    const routeNames = new Set(Array.from(this.stops.values()).map((s) => this.utilsService.normalizeStopName(s.name)).filter(Boolean));
    const townByKey = new Map<string, number>();
    const townByName = new Map<string, number>();
    this.townStops.forEach((stop, id) => {
      townByKey.set(keyOf(stop.lat, stop.lng), id);
      const name = this.utilsService.normalizeStopName(stop.name);
      if (name) townByName.set(name, id);
    });

    for (const row of rows) {
      if (isNaN(row.latitude) || isNaN(row.longitude)) continue;
      const key = keyOf(row.latitude, row.longitude);
      const name = this.utilsService.normalizeStopName(row.name);
      if (routeKeys.has(key) || (name && routeNames.has(name))) continue;
      routeKeys.add(key);
      if (name) routeNames.add(name);

      const townId = townByKey.get(key) ?? (name ? townByName.get(name) : undefined);
      if (townId !== undefined) {
        this.convertToNormalStop(townId, true);
        townByKey.delete(key);
        if (name) townByName.delete(name);
      } else {
        const added = this.createOptions.addStop(this.map, { lat: row.latitude, lng: row.longitude }, this.mapConfig.radius, row.name);
        this.sessionStopIds.add(Number(added.getProperty('id')));
      }
    }

    await this.loadSegmentAndStops(true, this.map);
  }

  public async replaceRouteWithImport(features: any[]): Promise<void> {
    const preservedTownStops = this.collectNonSessionStopsAsTownStops();
    this.clearMap([FeatureType.SEGMENT, FeatureType.STOP]);
    if (features.length) {
      this.map.data.addGeoJson(this.utilsService.syncTerrainSegmentEndpoints({ type: 'FeatureCollection', features }));
    }
    await this.loadSegmentAndStops(true, this.map);
    this.sessionStopIds = new Set(this.stops.keys());
    if (preservedTownStops.length) await this.loadTownStops(preservedTownStops);
  }

  private collectNonSessionStopsAsTownStops(): any[] {
    return Array.from(this.stops.values())
      .filter((stop) => !this.sessionStopIds.has(stop.id))
      .map((stop) => ({ properties: { ...stop }, geometry: { coordinates: [stop.lng, stop.lat] } }));
  }

  // A stop id can only live in one of the two maps: the route copy wins over the town copy
  public removeDuplicatedTownStops(): void {
    if (!this.map || this.townStops.size === 0) return;
    this.townStops.forEach((_, id) => {
      if (!this.stops.has(id)) return;
      this.removeTownStopFromMap(id);
    });
  }

  private removeTownStopFromMap(id: number): void {
    const marker = this.townStopsMarkers.get(id);
    if (marker) {
      marker.map = null;
      this.detachMarkerListeners(id, marker);
    }
    this.townStopsMarkers.delete(id);
    this.townStops.delete(id);
    this.deleteOptions.removeElement(this.map, id, FeatureType.TOWN_STOP);
  }

  private convertToNormalStop(stopId: number, force = false) {
    if (this.isTownStopVisible(stopId) || force) {
      if (!this.stops.get(stopId)) {
        const townStop = this.townStops.get(stopId);
        if (!townStop) return;
        const newFeature = this.editOptions.editElement(this.map, FeatureType.TOWN_STOP, stopId, 'type', FeatureType.STOP);
        townStop.type = FeatureType.STOP;
        this.stops.set(stopId, townStop);
        this.townStops.delete(stopId);
        const selectedMarker = this.townStopsMarkers.get(stopId);
        if (selectedMarker) {
          this.detachMarkerListeners(stopId);
          selectedMarker.gmpDraggable = this.canEditSharedStopFields(stopId);
          selectedMarker.gmpClickable = true;
          selectedMarker.map = this.mapConfig.showRouteStops ? this.map : null;
          this.stopsMarkers.set(stopId, selectedMarker);
          this.townStopsMarkers.delete(stopId);
          this.setStopCircle(townStop);
          const feature = this.utilsService.findFeatureByPropertyId(this.map.data, stopId, FeatureType.STOP);
          if (feature) this.attachMarkerEvents(stopId, selectedMarker, townStop, feature);
        }
        return newFeature;
      }
    }
  }

  // Route stops that ended up isolated are town stops again; called before every save from the design editor
  public demoteIsolatedRouteStops(demandStopsInRoute = false): number {
    const routeStops = Array.from(this.stops.values());
    const alternativeStopIds = new Set<number>(
      routeStops.flatMap((stop) => (stop.alternatives ?? []).map((alternative: any) => alternative.properties.id))
    );
    const isolatedStops = routeStops.filter((stop) =>
      !stop.start && !stop.end && !stop.inflexion && !stop.alternatives?.length &&
      !(demandStopsInRoute && stop.demand) &&
      !alternativeStopIds.has(stop.id) && this.getConnectedSegments(stop.id).length === 0
    );
    return this.demoteStopsToTown(isolatedStops.map((stop) => stop.id));
  }

  public demoteStopsToTown(stopIds: Iterable<number>): number {
    const demotedIds = [...stopIds].filter((stopId) => this.stops.has(stopId));
    demotedIds.forEach((stopId) => this.convertToTownStop(stopId));
    if (demotedIds.length) {
      this.selectedStops = this.selectedStops.filter((selected) => this.stops.has(Number(selected.getProperty('id'))));
      this.onSelectStops.next(this.selectedStops);
    }
    return demotedIds.length;
  }

  // Inverse of convertToNormalStop: the stop leaves the route but stays in the town, keeping only the shared fields
  private convertToTownStop(stopId: number): void {
    const stop = this.stops.get(stopId);
    if (!stop) return;
    ['start', 'end', 'inflexion'].forEach((flag) => this.editOptions.editElement(this.map, FeatureType.STOP, stopId, flag, false));
    this.editOptions.editElement(this.map, FeatureType.STOP, stopId, 'alternatives', null);
    this.editOptions.editElement(this.map, FeatureType.STOP, stopId, 'type', FeatureType.TOWN_STOP);
    const townStop = { ...stop, start: false, end: false, inflexion: false, alternatives: null, connections: [], type: FeatureType.TOWN_STOP };
    this.stops.delete(stopId);
    this.townStops.set(stopId, townStop);
    this.removeStopCircle(stopId);
    const marker = this.stopsMarkers.get(stopId);
    this.stopsMarkers.delete(stopId);
    if (marker) {
      this.detachMarkerListeners(stopId);
      this.townStopsMarkers.set(stopId, marker);
      marker.map = this.isTownStopVisible(stopId) ? this.map : null;
      const feature = this.utilsService.findFeatureByPropertyId(this.map.data, stopId, FeatureType.TOWN_STOP);
      if (feature) {
        this.map.data.overrideStyle(feature, { visible: false });
        this.attachMarkerEvents(stopId, marker, townStop, feature);
      }
      this.updateMarkerIcon(stopId, StopMarkerStatus.DEFAULT);
    }
  }

  public attachMarkerEvents(markerId: number, marker: google.maps.marker.AdvancedMarkerElement, stop: any, feature: google.maps.Data.Feature) {
    this.detachMarkerListeners(markerId);
    const listeners: { element: any, event: string, handler: any }[] = [];
    this.markerDomListeners.set(markerId, listeners);
    const clickHandler = async () => {
      if (this.creationOutline) return;
      const selectedIds = this.selectedStops.map((stop) => Number(stop.getProperty('id')));
      const clickedStop = this.stops.get(markerId) || this.townStops.get(markerId);
      if (this.creationSegments) {
        if (!this.startStop) {
          this.clearSelectedSegments();
          this.somePoints = [];
          this.coordinates = [];
          const startStopCoords = { lat: clickedStop.lat, lng: clickedStop.lng };
          this.startStop = {id: clickedStop.id, ...startStopCoords};
          this.coordinates.push(this.startStop);
          this.originStop = this.createOptions.buildPointFeature(clickedStop.id!, startStopCoords, clickedStop);
          if (selectedIds.length > 0) {
            selectedIds.forEach((id) => this.updateMarkerIcon(id, StopMarkerStatus.DEFAULT));
          }
          this.selectedStops = [];
          if (this.townStops.has(markerId)) feature = this.convertToNormalStop(clickedStop.id);
          this.selectedStops.push(feature);
          this.updateMarkerIcon(markerId, StopMarkerStatus.SELECTED);
          this.onSelectStops.next(this.selectedStops);
          this.onFlightPathChange.next();
        } else {
          if (this.startStop.id === clickedStop.id) return;
          const endStopCoords = { lat: clickedStop.lat, lng: clickedStop.lng };
          this.endStop = {id: clickedStop.id, ...endStopCoords};
          this.coordinates.push(this.endStop);
          this.destinationStop = this.createOptions.buildPointFeature(clickedStop.id!, endStopCoords, clickedStop);
          if (this.townStops.has(markerId)) feature = this.convertToNormalStop(clickedStop.id);
          if (this.selectedSegments.length > 0) {
            this.selectedSegments.forEach((segment) => this.map.data.overrideStyle(segment, this.defaultSegmentStyles));
          }
          const startStop = this.startStop;
          const endStop = this.endStop;
          const originStop = this.originStop;
          const destinationStop = this.destinationStop;
          this.startStop = null;
          this.endStop = null;
          this.originStop = null;
          this.destinationStop = null;
          try {
            const newSegment = await this.createOptions.addSegment(this.map, startStop, endStop, originStop, destinationStop, this.somePoints, this.creationSegmentsType, this.mapConfig.drivingSpeed!)
            this.selectedSegments.push(newSegment);
            this.clearFlightPath();
            if (this.selectedStops.length > 0) {
              this.selectedStops.forEach((stop) => { this.updateMarkerIcon(Number(stop.getProperty('id')), StopMarkerStatus.DEFAULT); });
            }
            this.selectedStops = [];
            this.onSelectStops.next(this.selectedStops);
            await this.loadSegmentAndStops();
            this.onUpdateSegment.next(this.selectedSegments);
            if (this.isRedrawingSegment) {
              this.isRedrawingSegment = false;
              this.changeCreationSegments(false);
            }
          } catch {
            this.startStop = startStop;
            this.originStop = originStop;
            this.clearFlightPath();
            this.coordinates.push(this.startStop);
          }
        }
      } else if (this.isSettingMergeStop) {
        this.ngZone.run(() => {
          // Merging must not pull town-only stops into the route: they keep their TOWN_STOP type
          // (excluded on save) and the backend replicates the merge through the bidirectional reference
          this.updateMarkerIcon(markerId, StopMarkerStatus.MERGE_SELECTED);
          this.edit(markerId, FeatureType.STOP, 'merge', clickedStop);
          this.showRelatedStopsOfType(StopType.MERGE);
          this.isSettingMergeStop = false;
          this.restoreCursor();
        });
      } else if (this.isSettingAlternativeStop) {
        this.ngZone.run(() => {
          this.convertToNormalStop(Number(this.selectedStops[0].getProperty('id')));
          if (this.townStops.has(markerId)) feature = this.convertToNormalStop(clickedStop.id);
          this.updateMarkerIcon(markerId, StopMarkerStatus.ALTERNATIVES_SELECTED);
          this.edit(markerId, FeatureType.STOP, 'alternatives', clickedStop);
          this.showRelatedStopsOfType(StopType.ALTERNATIVES);
          this.isSettingAlternativeStop = false;
          this.restoreCursor();
        });
      } else {
        if (this.editingWaypointsSegmentId !== null) this.clearSelectedSegments();
        if (selectedIds.includes(markerId) && !this.isShowingSpecificStopType && this.shiftPressed) {
          this.updateMarkerIcon(markerId, StopMarkerStatus.DEFAULT);
          const index = this.selectedStops.findIndex((stop) => Number(stop.getProperty('id')) === markerId);
          this.selectedStops.splice(index, 1);
          this.showRelatedStopsOfType(null);
        } else {
          if (!this.shiftPressed && selectedIds.length > 0) {
            this.clearSelectedStops();
          } else if (this.shiftPressed) {
            if (this.originalSelectedStopId) {
              const originalFeature = this.utilsService.findFeatureByPropertyId(this.map.data, this.originalSelectedStopId, FeatureType.STOP);
              this.selectedStops = originalFeature ? [originalFeature] : [];
              this.originalSelectedStopId = null;
            }
            this.stops.forEach((stop) => {
              if (!stop.demand) this.updateMarkerIcon(stop.id, StopMarkerStatus.DEFAULT);
            });
            this.selectedStops.forEach((s) => this.updateMarkerIcon(Number(s.getProperty('id')), StopMarkerStatus.SELECTED));
            this.isShowingSpecificStopType = null;
          }
          if (!this.selectedStops.some(feature => feature.getProperty('id') === markerId)) {
            if (!feature) feature = this.utilsService.findFeatureByPropertyId(this.map.data, markerId, FeatureType.TOWN_STOP)!;
            this.selectedStops.push(feature);
            this.updateMarkerIcon(markerId, StopMarkerStatus.SELECTED);
          }
        }
        this.onSelectStops.next(this.selectedStops);
      }
      (marker as unknown as HTMLElement).style.cursor = this.buildCursor(images.cursor.hover);
    };
    marker.addEventListener('gmp-click', clickHandler);
    listeners.push({ element: marker, event: 'gmp-click', handler: clickHandler });

    const rightClickHandler = (event: any) => {
      if (!marker.gmpClickable) return;
      const selectedStop = this.selectedStops[0];
      const alternatives: any = selectedStop?.getProperty('alternatives');
      const left = event.clientX;
      const top = event.clientY;
      if (alternatives?.some((alternative: any) => alternative.properties.id === markerId)) {
        if (this.menuStopsBox) this.menuStopsBox.style.display = "";
        this.menuAlternativeBox = document.getElementById("menu-stop-alternative");
        this.menuAlternativeBox!.style.display = "flex";
        this.utilsService.positionMenuWithinViewport(this.menuAlternativeBox!, left, top);
        const menuItem = this.menuAlternativeBox?.querySelector('#removeAlternative');
        if (this.removeAlternativeHandler) {
          menuItem?.removeEventListener('click', this.removeAlternativeHandler);
        }
        this.removeAlternativeHandler = () => {
          this.removeAlternativeStop(markerId);
          this.menuAlternativeBox!.style.display = "";
        };
        menuItem?.addEventListener('click', this.removeAlternativeHandler, { once: true });
      } else {
        if (this.menuAlternativeBox) this.menuAlternativeBox.style.display = "";
        this.clearSelectedStops();
        this.setSelectedStop({ids: [markerId], multiple: false});

        this.menuStopsBox = document.getElementById("menu-stops");
        this.menuStopsBox!.style.display = "flex";
        this.utilsService.positionMenuWithinViewport(this.menuStopsBox!, left, top);
      }
      event.preventDefault();
    };
    marker.addEventListener('contextmenu', rightClickHandler);
    listeners.push({ element: marker, event: 'contextmenu', handler: rightClickHandler });

    const mouseoverHandler = () => {
      if (this.isBoxSelecting) return;
      const currentStop = this.stops.get(markerId) ?? this.townStops.get(markerId);
      if (!currentStop || !this.canHoverStop(currentStop)) return;
      let cursorImage = this.buildCursor(images.cursor.hover);
      if (this.creationSegments || this.creationOutline || this.isSettingMergeStop || this.isSettingAlternativeStop || this.shiftPressed) cursorImage = this.activeCursor;
      (marker as unknown as HTMLElement).style.cursor = cursorImage;
      const isSelected = this.selectedStops.some((s) => Number(s.getProperty('id')) === markerId);
      if (this.isHovering === markerId || isSelected || this.highlightedStops.ids.includes(markerId)) return;
      this.isHovering = markerId;
      this.updateMarkerIcon(markerId, StopMarkerStatus.HOVER);
    };
    marker.addEventListener('mouseover', mouseoverHandler);
    listeners.push({ element: marker, event: 'mouseover', handler: mouseoverHandler });

    const mouseleaveHandler = () => {
      if (this.isBoxSelecting) return;
      if (this.isHovering !== markerId) return;
      this.isHovering = null;
      const currentStop = this.stops.get(markerId) ?? this.townStops.get(markerId);
      if (!currentStop || !this.canHoverStop(currentStop)) return;
      const isSelected = this.selectedStops.some((s) => Number(s.getProperty('id')) === markerId);
      if (isSelected || this.highlightedStops.ids.includes(markerId)) return;
      this.updateMarkerIcon(markerId, StopMarkerStatus.DEFAULT);
    };
    marker.addEventListener('mouseleave', mouseleaveHandler);
    listeners.push({ element: marker, event: 'mouseleave', handler: mouseleaveHandler });

    const mousedownHandler = () => {
      if (!marker.gmpDraggable) return;
      (marker as unknown as HTMLElement).style.cursor = this.buildCursor(images.cursor.drag);
    };
    marker.addEventListener('mousedown', mousedownHandler);
    listeners.push({ element: marker, event: 'mousedown', handler: mousedownHandler });

    let dragStartCoords: {lat: number, lng: number} | null = null;
    const dragstartHandler = () => {
      if (this.shiftPressed) return;
      const current = this.stops.get(stop.id) ?? this.townStops.get(stop.id);
      dragStartCoords = current ? { lat: current.lat, lng: current.lng } : null;
      this.setSelectedStop({ ids: [markerId], multiple: false });
    };
    marker.addListener('dragstart', dragstartHandler);
    listeners.push({ element: marker, event: 'dragstart', handler: dragstartHandler });

    const dragendHandler = (event: google.maps.MapMouseEvent) => {
      (marker as unknown as HTMLElement).style.cursor = this.buildCursor(images.cursor.hover);
      const newCoords = {lat: event.latLng?.lat()!, lng: event.latLng?.lng()!};
      const TOLERANCE = 1e-7;
      if (dragStartCoords &&
          Math.abs(dragStartCoords.lat - newCoords.lat) < TOLERANCE &&
          Math.abs(dragStartCoords.lng - newCoords.lng) < TOLERANCE) {
        marker.position = new google.maps.LatLng(dragStartCoords.lat, dragStartCoords.lng);
        dragStartCoords = null;
        return;
      }
      dragStartCoords = null;
      const isTownStop = !this.stops.has(stop.id) && this.townStops.has(stop.id);
      this.editOptions.moveStop(this.map, stop.id, newCoords, isTownStop ? FeatureType.TOWN_STOP : FeatureType.STOP);
      this.stopCircles.get(stop.id)?.setCenter(newCoords);
      this.syncGhostPolylinesForStop(stop.id);
      const updatedStop = { ...(this.stops.get(stop.id) ?? this.townStops.get(stop.id)), ...newCoords };
      (isTownStop ? this.townStops : this.stops).set(stop.id, updatedStop);
      this.nameLabelsService.updatePosition(stop.id, new google.maps.LatLng(newCoords.lat, newCoords.lng), this.getNamedStops());
      this.onUpdateStop.next(updatedStop);
      this.onStopDragged.next({ id: updatedStop.id, name: updatedStop.name });
    };
    marker.addListener('dragend', dragendHandler);
    listeners.push({ element: marker, event: 'dragend', handler: dragendHandler });

    const documentDragEndFallback = () => {
      document.body.style.cursor = this.buildCursor(images.cursor.default);
      if (dragStartCoords === null) return;
      setTimeout(() => {
        if (dragStartCoords !== null) {
          dragStartCoords = null;
          const wasDraggable = marker.gmpDraggable;
          marker.gmpDraggable = false;
          requestAnimationFrame(() => { marker.gmpDraggable = wasDraggable; });
        }
      }, 0);
    };
    document.addEventListener('pointerup', documentDragEndFallback);
    document.addEventListener('mouseup', documentDragEndFallback);
    listeners.push({ element: document, event: 'pointerup', handler: documentDragEndFallback });
    listeners.push({ element: document, event: 'mouseup', handler: documentDragEndFallback });
  }

  private canHoverStop(stop: any): boolean {
    if (this.creationOutline) return false;
    if (this.isShowingSpecificStopType) {
      if (this.isShowingSpecificStopType === StopType.ALTERNATIVES) {
        return !this.selectedStops.some((selectedStop) => {
          const alternatives: any = selectedStop.getProperty('alternatives') || [];
          return alternatives.some((a: any) => a.properties.id === stop.id);
        });
      }
      return !stop[this.isShowingSpecificStopType];
    }
    return !stop.demand;
  }

  // MARKER ACTIONS
  setStartStop() { this.setExclusiveStopFlag('start'); }
  setEndStop() { this.setExclusiveStopFlag('end'); }
  setDemandStop() { this.setToggleStopFlag('demand'); }
  setInflexionStop() { this.setToggleStopFlag('inflexion'); }

  cancelOnGoingStopAction() {
    if (this.isSettingMergeStop) {
      this.isSettingMergeStop = false;
      this.restoreCursor();
    }
    if (this.isSettingAlternativeStop) {
      this.isSettingAlternativeStop = false;
      this.restoreCursor();
    }
  }

  private buildCursor(url: string, x = 6, y = 6): string {
    return `url(${url}) ${x} ${y}, auto`;
  }

  restoreCursor(): void {
    this.activeCursor = this.buildCursor(images.cursor.default);
    this.map.setOptions({ draggableCursor: this.activeCursor });
    this.map.data.forEach(feature => this.map.data.overrideStyle(feature, { cursor: this.activeCursor }));
    this.forceCursor();
  }

  private forceCursor(): void {
    const mapDiv = this.map.getDiv();
    const overlay = document.createElement('div');
    overlay.style.cssText = `position:absolute;inset:0;z-index:9999;cursor:${this.activeCursor};pointer-events:all;`;
    mapDiv.appendChild(overlay);
    requestAnimationFrame(() => overlay.remove());
  }

  private setExclusiveStopFlag(flag: 'start' | 'end') {
    this.convertToNormalStop(Number(this.selectedStops[0].getProperty('id')));
    const selectedStatus = flag === 'start' ? StopMarkerStatus.START_SELECTED : StopMarkerStatus.END_SELECTED;
    const stop = this.stops.get(Number(this.selectedStops[0].getProperty('id')))!;
    stop[flag] = !stop[flag];
    if (stop[flag]) {
      for (const otherStop of this.stops.values()) {
        if (otherStop[flag] && otherStop.id !== stop.id) {
          otherStop[flag] = false;
          this.editOptions.editElement(this.map, FeatureType.STOP, otherStop.id, flag, false);
          break;
        }
      }
    }
    if (this.isShowingSpecificStopType) this.clearSelectedStopType();
    this.selectedStops[0] = this.editOptions.editElement(this.map, FeatureType.STOP, stop.id, flag, stop[flag]);
    this.updateMarkerIcon(stop.id, stop[flag] ? selectedStatus : StopMarkerStatus.SELECTED);
    this.onSelectStops.next(this.selectedStops);
    this.onUpdateStop.next(stop);
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
  }

  private setToggleStopFlag(flag: 'demand' | 'inflexion') {
    this.convertToNormalStop(Number(this.selectedStops[0].getProperty('id')));
    const selectedStatus = flag === 'demand' ? StopMarkerStatus.DEMAND_DEFAULT : StopMarkerStatus.INFLEXION_SELECTED;
    const stop = this.stops.get(Number(this.selectedStops[0].getProperty('id')))!;
    stop[flag] = !stop[flag];
    if (this.isShowingSpecificStopType) this.clearSelectedStopType();
    this.selectedStops[0] = this.editOptions.editElement(this.map, FeatureType.STOP, stop.id, flag, stop[flag]);
    this.updateMarkerIcon(stop.id, stop[flag] ? selectedStatus : StopMarkerStatus.SELECTED);
    this.onSelectStops.next(this.selectedStops);
    this.onUpdateStop.next(stop);
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
  }

  setMergeStop() {
    this.isSettingMergeStop = true;
    this.activeCursor = this.buildCursor(images.cursor.merge);
    this.map.setOptions({ draggableCursor: this.activeCursor });
    this.forceCursor();
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
  }

  unsetMergeStop() {
    const selectedId = Number(this.selectedStops[0].getProperty('id'));
    const selectedStop = this.stops.get(selectedId) ?? this.townStops.get(selectedId);
    if (!selectedStop?.bidirectional) return;
    const selectedFeatureType = this.stops.has(selectedId) ? FeatureType.STOP : FeatureType.TOWN_STOP;

    const bidirectionalStopId = selectedStop.bidirectional.properties.id;
    const mergedStop = this.stops.get(bidirectionalStopId) ?? this.townStops.get(bidirectionalStopId);
    if (mergedStop) {
      const mergedStopFeatureType = this.stops.has(bidirectionalStopId) ? FeatureType.STOP : FeatureType.TOWN_STOP;
      mergedStop.merge = null;
      mergedStop.bidirectional = null;
      this.editOptions.editElement(this.map, mergedStopFeatureType, mergedStop.id, 'merge', null);
      this.editOptions.editElement(this.map, mergedStopFeatureType, mergedStop.id, 'bidirectional', null);
      this.reattachMarkerEvents(mergedStop.id, mergedStop);
      this.updateMarkerIcon(mergedStop.id, StopMarkerStatus.DEFAULT);
    }

    selectedStop.merge = null;
    selectedStop.bidirectional = null;
    this.editOptions.editElement(this.map, selectedFeatureType, selectedStop.id, 'merge', null);
    const updatedStop = this.editOptions.editElement(this.map, selectedFeatureType, selectedStop.id, 'bidirectional', null);
    this.reattachMarkerEvents(selectedStop.id, selectedStop);

    this.loadSegmentAndStops().then(() => this.onUpdateStop.next(updatedStop));
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
  }

  setAlternativeStop() {
    this.isSettingAlternativeStop = true;
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
    this.activeCursor = this.buildCursor(images.cursor.alternative);
    this.map.setOptions({ draggableCursor: this.activeCursor });
    this.forceCursor();
  }

  removeAllAlternativeStops() {
    const selectedStop = this.stops.get(Number(this.selectedStops[0].getProperty('id')))!;
    selectedStop.alternatives.forEach((alternative: any) => this.updateMarkerIcon(alternative.properties.id, StopMarkerStatus.DEFAULT));
    selectedStop.alternatives = null;
    const updatedStop = this.editOptions.editElement(this.map, FeatureType.STOP, selectedStop.id, 'alternatives', null);
    this.loadSegmentAndStops().then(() => this.onUpdateStop.next(updatedStop));
    if (this.menuStopsBox) this.menuStopsBox.style.display = "";
  }

  removeAlternativeStop(markerId: number) {
    const selectedStop = this.stops.get(Number(this.selectedStops[0].getProperty('id')))!;
    const newAlternatives = selectedStop.alternatives.filter((alt: any) => alt.properties.id !== markerId);
    selectedStop.alternatives = newAlternatives.length > 0 ? newAlternatives : null;
    this.updateMarkerIcon(markerId, StopMarkerStatus.DEFAULT);
    const updatedStop = this.editOptions.editElement(this.map, FeatureType.STOP, selectedStop.id, 'alternatives', selectedStop.alternatives);
    this.loadSegmentAndStops().then(() => this.onUpdateStop.next(updatedStop));
  }

  private getSegmentProperties(id: number, feature: any) {
    const simpleKeys = ['connectionIds', 'distance', 'intersectionIds', 'polyline', 'townSegmentIds', 'type', 'weight'] as const;
    const weight = feature.getProperty('weight');
    const start = this.resolveSegmentEndpoint(feature.getProperty('start'));
    const end = this.resolveSegmentEndpoint(feature.getProperty('end'));

    return {
      ...Object.fromEntries(simpleKeys.map(k => [k, feature.getProperty(k)])),
      start,
      end,
      id,
      name: `${this.getStopDisplayName(start.properties)}\n${this.getStopDisplayName(end.properties)}`,
      formattedTime: this.utilsService.convertSecondsToMinutesAndSeconds(weight, false)
    };
  }

  // The embedded copy is only a fallback for endpoints that are not on the map.
  private resolveSegmentEndpoint(endpoint: any) {
    const stopId = endpoint?.properties?.id;
    if (stopId == null) return endpoint;
    const stopFeature = this.utilsService.findFeatureByPropertyId(this.map.data, stopId, FeatureType.STOP)
      ?? this.utilsService.findFeatureByPropertyId(this.map.data, stopId, FeatureType.TOWN_STOP);
    if (!stopFeature) return endpoint;

    let resolved = endpoint;
    stopFeature.toGeoJson((stopJson: any) => {
      resolved = {
        ...endpoint,
        properties: stopJson.properties ?? endpoint.properties,
        geometry: stopJson.geometry ?? endpoint.geometry,
      };
    });
    return resolved;
  }

  private getStopDisplayName(properties: any) {
    return `${properties.code ? `${properties.code} - ` : ''}${properties.name}`;
  }

  private getConnectedSegments(stopId: number): IBase[] {
    const connections: { segment: IBase; isStart: boolean; counterpartName: string }[] = [];
    this.segments.forEach((segment, segId) => {
      const isStart = segment.start.properties.id === stopId;
      if (!isStart && segment.end.properties.id !== stopId) return;
      connections.push({
        segment: { id: segId, name: segment.name },
        isStart,
        counterpartName: this.getStopDisplayName(isStart ? segment.end.properties : segment.start.properties),
      });
    });
    return connections
      .sort((a, b) => Number(b.isStart) - Number(a.isStart) || a.counterpartName.localeCompare(b.counterpartName))
      .map((connection) => connection.segment);
  }

  private getStopProperties(id: number, feature: any) {
    const simpleKeys = ['alternatives', 'bidirectional', 'demand', 'direction', 'end', 'inflexion', 'merge', 'name', 'start', 'type', ...APP.TOWN_STOP_METADATA_PROPERTIES] as const;
    return {
      ...Object.fromEntries(simpleKeys.map(k => [k, feature.getProperty(k)])),
      code: feature.getProperty('code') || '',
      radius: feature.getProperty('radius') || APP.DEFAULT_STOP_RADIUS,
      id,
      lat: feature.getGeometry().elements.lat(),
      lng: feature.getGeometry().elements.lng()
    };
  }

  private getStopPropertiesFromObject(stop: any) {
    const props = stop.properties;
    return {
      ...stop.properties,
      code: props.code || '',
      radius: props.radius || APP.DEFAULT_STOP_RADIUS,
      lat: stop.geometry.coordinates[1],
      lng: stop.geometry.coordinates[0]
    }
  }

  async loadSegmentAndStops(editable: boolean = true, map?: google.maps.Map) {
    if (map) this.map = map;
    this.segments = new Map();
    this.stops = new Map();

    const AdvancedMarkerElement = await this.markerRenderService.getAdvancedMarkerElementClass();

    this.map.data.forEach((feature: any) => {
      if (feature.getProperty('type') === FeatureType.SEGMENT) {
        const segId = Number(feature.getProperty('id'));
        this.segments.set(segId, this.getSegmentProperties(segId, feature));
      }
    });

    this.map.data.forEach((feature: any) => {
      if (feature.getProperty('type') === FeatureType.STOP) {
        this.map.data.overrideStyle(feature, { visible: false });
        const id = feature.getProperty('id');
        const stop = {
          ...this.getStopProperties(id, feature),
          connections: this.getConnectedSegments(id)
        };
        this.stops.set(stop.id, stop);
        if (!this.stopsMarkers.has(stop.id)) this.createMarkerSync(stop, editable, editable, this.stopsMarkers, AdvancedMarkerElement);
      }
    });

    await this.renderStopNames();
  }

  private async renderStopNames(): Promise<void> {
    if (!this.mapConfig.showNames) return;
    this.nameLabelsService.setMap(this.map);
    await this.nameLabelsService.render(this.getNamedStops());
  }

  private getNamedStops(): Map<number, any> {
    const visibleTownStops = [...this.townStops].filter(([id]) => this.isTownStopVisible(id));
    return new Map([...this.stops, ...visibleTownStops]);
  }

  async loadStops(editable: boolean = true, stopFeatures: any[] = [], map?: google.maps.Map, color?: string) {
    if (map) this.map = map;
    this.stops = new Map();
    const AdvancedMarkerElement = await this.markerRenderService.getAdvancedMarkerElementClass();
    stopFeatures.forEach(stop => {
      this.stops.set(stop.id, stop);
      if (!this.stopsMarkers.has(stop.id)) this.createMarkerSync(stop, editable, editable, this.stopsMarkers, AdvancedMarkerElement, color);
    });
  }

  clearMap(featureTypesToRemove: FeatureType[] = []) {
    const removeAll = featureTypesToRemove.length === 0;
    const shouldRemove = (type: FeatureType) => removeAll || featureTypesToRemove.includes(type);

    if (this.map) {
      const toRemove: google.maps.Data.Feature[] = [];
      this.map.data.forEach((feature) => {
        if (shouldRemove(feature.getProperty('type') as FeatureType)) toRemove.push(feature);
      });
      toRemove.forEach((feature) => this.map.data.remove(feature));
    }

    if (shouldRemove(FeatureType.SEGMENT)) {
      this.clearGhostPolylines();
      this.selectedSegments = [];
      this.segments = new Map();
    }
    if (shouldRemove(FeatureType.STOP)) {
      this.stopsMarkers.forEach((marker) => marker.map = null);
      this.stopsMarkers = new Map();
      this.clearStopCircles();
      this.nameLabelsService.clear();
      this.selectedStops = [];
      this.stops = new Map();
    }
    if (shouldRemove(FeatureType.TOWN_STOP)) {
      this.townStopsMarkers.forEach((marker) => marker.map = null);
      this.townStopsMarkers = new Map();
      this.townStops = new Map();
      this.townCatalogueLoaded = false;
    }
  }

  private createMarkerSync(
    stop: any,
    clickable: boolean,
    draggable: boolean,
    markersMap: Map<number, google.maps.marker.AdvancedMarkerElement>,
    AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement,
    color?: string
  ): void {
    const isSelected = this.selectedStops.some((s) => Number(s.getProperty('id')) === stop.id);
    let initialStatus = isSelected ? StopMarkerStatus.SELECTED : StopMarkerStatus.DEFAULT;
    if (!isSelected) {
      if (stop.demand) initialStatus = StopMarkerStatus.DEMAND_DEFAULT;
      else if (this.mapConfig.showEndStop && stop.end) initialStatus = StopMarkerStatus.END_SELECTED;
      else if (this.mapConfig.showStartStop && stop.start) initialStatus = StopMarkerStatus.START_SELECTED;
    }
    const element = this.markerRenderService.getMarkerElement(initialStatus, color);
    element.style.pointerEvents = 'auto';
    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: {lat: stop.lat, lng: stop.lng},
      gmpDraggable: draggable,
      gmpClickable: clickable,
      content: element,
      zIndex: (initialStatus in images.routes.stops || initialStatus === StopMarkerStatus.SELECTED) ? 10 : 0
    });
    marker.setAttribute('id', stop.id);
    const isRouteStop = markersMap === this.stopsMarkers;
    if (clickable || draggable) {
      const featureType = isRouteStop ? FeatureType.STOP : FeatureType.TOWN_STOP;
      this.attachMarkerEvents(stop.id, marker, stop, this.utilsService.findFeatureByPropertyId(this.map.data, stop.id, featureType)!);
    }
    markersMap.set(stop.id, marker);
    if (isRouteStop) this.setStopCircle(stop, color);
  }

  private setStopCircle(stop: any, color?: string): void {
    this.removeStopCircle(stop.id);
    const circle = this.markerRenderService.getStopCircle(stop, color ?? this.serviceColor);
    if (!circle) return;
    circle.setMap(this.areStopCirclesVisible() ? this.map : null);
    this.stopCircles.set(stop.id, circle);
  }

  private syncStopCircle(stop: any): void {
    const circle = this.stopCircles.get(stop.id);
    if (!circle) {
      this.setStopCircle(stop);
      return;
    }
    const radius = Number(stop.radius);
    if (!radius || radius <= 0) {
      this.removeStopCircle(stop.id);
      return;
    }
    circle.setCenter({ lat: stop.lat, lng: stop.lng });
    if (circle.getRadius() !== radius) circle.setRadius(radius);
  }

  private removeStopCircle(id: number): void {
    const circle = this.stopCircles.get(id);
    if (!circle) return;
    circle.setMap(null);
    this.stopCircles.delete(id);
  }

  private clearStopCircles(): void {
    this.stopCircles.forEach((circle) => circle.setMap(null));
    this.stopCircles.clear();
  }

  private areStopCirclesVisible(): boolean {
    return this.mapConfig.showRadius && this.mapConfig.showRouteStops;
  }

  private applyStopCirclesVisibility(): void {
    const target = this.areStopCirclesVisible() ? this.map : null;
    this.stopCircles.forEach((circle) => circle.setMap(target));
  }

  async addMarker(stop: any, clickable: boolean = true, draggable: boolean, markersMap: Map<number, google.maps.marker.AdvancedMarkerElement>, color?: string) {
    const AdvancedMarkerElement = await this.markerRenderService.getAdvancedMarkerElementClass();
    this.createMarkerSync(stop, clickable, draggable, markersMap, AdvancedMarkerElement, color);
  }

  public detachDataLayerEvents(): void {
    this.isDataLayerAttached = false;
    document.removeEventListener('keydown', this.keydownHandler);
    document.removeEventListener('keyup', this.keyupHandler);
    window.removeEventListener('blur', this.windowBlurHandler);
    this.shiftPressed = false;
    this.cancelBoxSelect();

    this.listeners.forEach(listener => listener.listener.remove());
    this.listeners = [];
    this.clearGhostPolylines();

    this.markerDomListeners.forEach((listeners) => {
      listeners.forEach(({ element, event, handler }) => element.removeEventListener(event, handler));
    });
    this.markerDomListeners.clear();

    [this.stopsMarkers, this.townStopsMarkers].forEach((markers) => markers.forEach((marker) => {
      marker.gmpDraggable = false;
      marker.gmpClickable = false;
    }));
  }

  public updateMarkers(): void {
    const idsToRemove: number[] = [];
    this.stopsMarkers.forEach((marker, id) => {
      if (!this.stops.has(id)) {
        marker.map = null;
        idsToRemove.push(id);
      } else {
        const stop = this.stops.get(id)!;
        marker.position = new google.maps.LatLng(stop.lat, stop.lng);
        this.syncStopCircle(stop);
        this.nameLabelsService.setPosition(id, new google.maps.LatLng(stop.lat, stop.lng));
      }
    });
    idsToRemove.forEach((id) => {
      this.stopsMarkers.delete(id);
      this.removeStopCircle(id);
    });
    if (this.mapConfig.showNames) this.nameLabelsService.cluster(this.getNamedStops());
  }

  private resolveStopMarkerStatus(markerId: number, stop: any, requestedStatus: string): string {
    let status = requestedStatus;
    if (stop.demand) {
      status = StopMarkerStatus.DEMAND_DEFAULT;
      if (this.selectedStops.some((s) => Number(s.getProperty('id')) === markerId)) {
        status = StopMarkerStatus.DEMAND_SELECTED;
      } else if (this.isShowingSpecificStopType) {
        status = StopMarkerStatus.DEMAND;
      }
    }
    if (!this.isShowingSpecificStopType) {
      if (this.mapConfig.showEndStop && stop.end) status = StopMarkerStatus.END_SELECTED;
      if (this.mapConfig.showStartStop && stop.start) status = StopMarkerStatus.START_SELECTED;
    }
    if (requestedStatus === StopMarkerStatus.SELECTED && this.selectedStops.length === 1 && !this.isShowingSpecificStopType) {
      let relatedType = null;
      if (stop.merge) {
        relatedType = StopType.MERGE;
      } else if (stop.start) {
        status = StopMarkerStatus.START_SELECTED;
      } else if (stop.end) {
        status = StopMarkerStatus.END_SELECTED;
      } else if (stop.inflexion) {
        status = StopMarkerStatus.INFLEXION_SELECTED;
      } else if (stop.alternatives?.length) {
        relatedType = StopType.ALTERNATIVES;
        status = StopMarkerStatus.SELECTED;
      } else {
        let isAlternativeOf = false;
        for (const s of this.stops.values()) {
          if (s.alternatives?.some((a: any) => a.properties.id === stop.id)) { isAlternativeOf = true; break; }
        }
        if (isAlternativeOf) {
          relatedType = StopType.ALTERNATIVE_OF;
          status = StopMarkerStatus.ALTERNATIVES_SELECTED;
        }
      }
      if (relatedType === StopType.MERGE || relatedType === StopType.ALTERNATIVES || relatedType === StopType.ALTERNATIVE_OF) {
        this.showRelatedStopsOfType(relatedType);
      } else {
        this.clearRelatedStops();
      }
    }
    if (this.legendType === LegendType.STOP_INFO && markerId === this.originalSelectedStopId) {
      status = this.highlightedStops.selectedStatus;
    }
    return status;
  }

  private updateMarkerIcon(markerId: number, status: string) {
    const marker = this.stopsMarkers.get(markerId) ?? this.townStopsMarkers.get(markerId);
    if (!marker) return;
    const stop = this.stops.get(markerId) ?? this.townStops.get(markerId);
    if (!stop) return;
    if (!this.stops.has(markerId)) {
      const townStopElement = this.markerRenderService.getMarkerElement(status);
      townStopElement.style.pointerEvents = 'auto';
      marker.content = townStopElement;
      marker.zIndex = status in images.routes.stops ? 10 : 0;
      if (status === StopMarkerStatus.SELECTED && this.selectedStops.length === 1 && !this.isShowingSpecificStopType && stop.merge) {
        this.showRelatedStopsOfType(StopType.MERGE);
      }
      return;
    }
    const resolvedStatus = this.resolveStopMarkerStatus(markerId, stop, status);
    const stopElement = this.markerRenderService.getMarkerElement(resolvedStatus, stop.color);
    stopElement.style.pointerEvents = 'auto';
    marker.content = stopElement;
    marker.zIndex = (resolvedStatus in images.routes.stops || resolvedStatus === StopMarkerStatus.SELECTED) ? 10 : 0;
  }

  private drawFlightPath(coordinates: google.maps.LatLng[]) {
    this.drawnSegments.forEach((segment) => segment.setMap(null));
    this.drawnSegments = [];
    const flightPath = new google.maps.Polyline({
      path: coordinates,
      geodesic: true,
      strokeColor: "#8136db",
      strokeOpacity: 1.0,
      strokeWeight: 4,
      zIndex: 2000
    });
    this.drawnSegments.push(flightPath);
    flightPath.setMap(this.map);
    flightPath.addListener('mouseover', () => (flightPath as any).setOptions({ cursor: this.activeCursor }));
    flightPath.addListener('mouseout', () => (flightPath as any).setOptions({ cursor: this.activeCursor }));
    this.onFlightPathChange.next();
  }

  private clearFlightPath() {
    this.drawnSegments.forEach((segment) => segment.setMap(null));
    this.drawnSegments = [];
    this.coordinates = [];
    this.somePoints = [];
  }

  private onSegmentClick(feature: google.maps.Data.Feature): void {
    const selectedSegmentIds = this.selectedSegments.map((segment) => segment.getProperty('id'));
    const featureId = feature.getProperty('id');

    if (selectedSegmentIds.includes(featureId) && this.shiftPressed) {
      const index = this.selectedSegments.findIndex((segment) => segment.getProperty('id') === featureId);
      this.selectedSegments.splice(index, 1);
      this.map.data.overrideStyle(feature, this.defaultSegmentStyles);
    } else {
      if (!this.shiftPressed && this.selectedSegments.length > 0) {
        selectedSegmentIds.forEach((id: any) => {
          this.map.data.overrideStyle(this.utilsService.findFeatureByPropertyId(this.map.data, id, FeatureType.SEGMENT)!, this.defaultSegmentStyles);
        });
        this.selectedSegments = [];
      }
      this.map.data.overrideStyle(feature, this.selectedSegmentStyles);
      this.selectedSegments.push(feature);
    }
    this.onSelectSegments.next(this.selectedSegments);
  }

  edit(id: number, type: FeatureType, valueToChange: string, value: any) {
    if (type === FeatureType.STOP) {
      this.editStop(id, valueToChange, value);
    } else {
      this.editSegment(id, valueToChange, value);
    }
  }

  private editStop(id: number, valueToChange: string, value: any) {
    const editedFeatureType = this.stops.has(id) ? FeatureType.STOP : FeatureType.TOWN_STOP;
    const editedFeature = this.editOptions.editElement(this.map, editedFeatureType, id, valueToChange, value);
    const editStop = (this.stops.get(id) ?? this.townStops.get(id))!;
    const stopMarker = (this.stopsMarkers.get(id) ?? this.townStopsMarkers.get(id))!;
    const selectedId = Number(this.selectedStops[0].getProperty('id'));
    const selectedFeatureType = this.stops.has(selectedId) ? FeatureType.STOP : FeatureType.TOWN_STOP;
    const selectedStop = (this.stops.get(selectedId) ?? this.townStops.get(selectedId))!;
    switch (valueToChange) {
      case 'lat':
      case 'lng':
        const newLatLng = new google.maps.LatLng(
          editedFeature.getGeometry().elements.lat(),
          editedFeature.getGeometry().elements.lng()
        );
        editStop.lat = newLatLng.lat();
        editStop.lng = newLatLng.lng();
        stopMarker.position = newLatLng;
        this.stopCircles.get(id)?.setCenter(newLatLng);
        this.nameLabelsService.updatePosition(id, newLatLng, this.getNamedStops());
        this.syncGhostPolylinesForStop(id);
        break;
      case 'radius':
        editStop[valueToChange] = Number(value);
        this.setStopCircle(editStop);
        break;
      case 'merge':
      case 'merge.properties.name':
        const mergeFeature = this.createOptions.buildMergedFeature(value);
        if (valueToChange === 'merge') {
          this.editOptions.editElement(this.map, selectedFeatureType, selectedId, 'merge', mergeFeature);
          selectedStop.merge = mergeFeature;
          this.editOptions.editElement(this.map, editedFeatureType, id, 'merge', mergeFeature);
          editStop.merge = mergeFeature;
          this.utilsService.findFeatureByPropertyId(this.map.data, id, editedFeatureType)?.toGeoJson((featureJson) => {
            this.editOptions.editElement(this.map, selectedFeatureType, selectedId, 'bidirectional', featureJson);
            selectedStop.bidirectional = featureJson;
          });
          this.utilsService.findFeatureByPropertyId(this.map.data, selectedId, selectedFeatureType)?.toGeoJson((featureJson) => {
            this.editOptions.editElement(this.map, editedFeatureType, id, 'bidirectional', featureJson);
            editStop.bidirectional = featureJson;
          });
        } else if (valueToChange === 'merge.properties.name') {
          this.editOptions.editElement(this.map, selectedFeatureType, selectedId, 'merge.properties.name', value);
          this.editOptions.editElement(this.map, editedFeatureType, id, 'merge.properties.name', value);
          editStop.merge.properties.name = value;
          selectedStop.merge.properties.name = value;
        }
        break;
      case 'alternatives':
        let alternatives = selectedStop.alternatives || [];
        const alternativeFeature = this.utilsService.findFeatureByPropertyId(this.map.data, value.id, FeatureType.STOP);
        alternativeFeature?.toGeoJson((featureJson) => {
          alternatives.push(featureJson);
          this.editOptions.editElement(this.map, FeatureType.STOP, selectedId, 'alternatives', alternatives);
        });
        selectedStop.alternatives = alternatives;
        break;
      case 'name':
      case 'code':
        this.applyStopLabelChange(id, editStop, valueToChange, value);
        break;
      default:
        editStop[valueToChange] = value;
        break;
    }
    if (valueToChange === 'merge' || valueToChange === 'merge.properties.name' || valueToChange === 'alternatives') {
      this.reattachMarkerEvents(selectedId, selectedStop);
    }
    this.reattachMarkerEvents(id, editStop);
    this.onUpdateStop.next(editStop);
  }

  // Name and code are denormalized into the connected segments' start/end copies
  private applyStopLabelChange(id: number, stop: any, property: 'name' | 'code', value: any): void {
    stop[property] = value;
    Array.from(this.segments.values())
      .filter((segment) => segment.start?.properties?.id === id || segment.end?.properties?.id === id)
      .forEach((segment) => {
        (['start', 'end'] as const).forEach((endpoint) => {
          if (segment[endpoint]?.properties?.id !== id) return;
          segment[endpoint].properties[property] = value;
          this.editOptions.editElement(this.map, FeatureType.SEGMENT, segment.id, `${endpoint}.properties.${property}`, value);
        });
      });
    this.refreshSegmentsForStop(id);
    if (this.mapConfig.showNames) this.nameLabelsService.update(id, this.getNamedStops());
  }

  private refreshSegmentsForStop(stopId: number): void {
    const refreshedIds: number[] = [];
    this.map.data.forEach((feature: any) => {
      if (feature.getProperty('type') !== FeatureType.SEGMENT) return;
      const startId = feature.getProperty('start')?.properties?.id;
      const endId = feature.getProperty('end')?.properties?.id;
      if (startId !== stopId && endId !== stopId) return;
      const segmentId = Number(feature.getProperty('id'));
      this.segments.set(segmentId, this.getSegmentProperties(segmentId, feature));
      refreshedIds.push(segmentId);
    });
    if (refreshedIds.length === 0) return;

    this.stops.forEach((stop) => {
      if (stop.connections?.some((connection: IBase) => refreshedIds.includes(Number(connection.id)))) {
        stop.connections = this.getConnectedSegments(stop.id);
      }
    });
  }

  private relocateRouteStop(id: number, coordinates: { lat: number, lng: number }): void {
    const stop = this.stops.get(id);
    if (!stop) return;
    this.editOptions.moveStop(this.map, id, coordinates, FeatureType.STOP);
    stop.lat = coordinates.lat;
    stop.lng = coordinates.lng;
    const latLng = new google.maps.LatLng(coordinates.lat, coordinates.lng);
    const marker = this.stopsMarkers.get(id);
    if (marker) marker.position = latLng;
    this.stopCircles.get(id)?.setCenter(latLng);
    this.nameLabelsService.updatePosition(id, latLng, this.getNamedStops());
    this.syncGhostPolylinesForStop(id);
  }

  // The town catalogue is the source of truth for the shared stop fields; a differing route copy is realigned on load
  private syncRouteStopWithTownStop(routeStop: any, townStop: any): void {
    const id = routeStop.id;
    if (townStop.name != null && townStop.name !== routeStop.name) {
      this.editOptions.editElement(this.map, FeatureType.STOP, id, 'name', townStop.name);
      this.applyStopLabelChange(id, routeStop, 'name', townStop.name);
    }
    if ((townStop.code ?? '') !== (routeStop.code ?? '')) {
      this.editOptions.editElement(this.map, FeatureType.STOP, id, 'code', townStop.code);
      this.applyStopLabelChange(id, routeStop, 'code', townStop.code);
    }
    const TOLERANCE = 1e-7;
    if (Math.abs(townStop.lat - routeStop.lat) > TOLERANCE || Math.abs(townStop.lng - routeStop.lng) > TOLERANCE) {
      this.relocateRouteStop(id, { lat: townStop.lat, lng: townStop.lng });
    }
    (['merge', 'bidirectional'] as const).forEach((property) => {
      const townValue = townStop[property] ?? null;
      const routeValue = routeStop[property] ?? null;
      const differs = townValue?.properties?.id !== routeValue?.properties?.id || townValue?.properties?.name !== routeValue?.properties?.name;
      if (!differs) return;
      this.editOptions.editElement(this.map, FeatureType.STOP, id, property, townValue);
      routeStop[property] = townValue;
    });
    APP.TOWN_STOP_METADATA_PROPERTIES.forEach((property) => {
      const townValue = townStop[property];
      if (townValue == null || townValue === routeStop[property]) return;
      this.editOptions.editElement(this.map, FeatureType.STOP, id, property, townValue);
      routeStop[property] = townValue;
    });
  }

  private editSegment(id: number, valueToChange: string, value: any) {
    const editSegment = this.segments.get(id)!;
    switch (valueToChange) {
      case 'time':
        const colon = value.includes(':');
        const formattedTime = colon ? value : `${value}:00`;
        const weight = this.utilsService.convertMinutesAndSecondsToSeconds(formattedTime);
        editSegment['weight'] = weight;
        editSegment['formattedTime'] = formattedTime;
        this.editOptions.editElement(this.map, FeatureType.SEGMENT, id, 'weight', weight.toString());
        break;
      case 'distance':
        this.editOptions.editElement(this.map, FeatureType.SEGMENT, id, 'distance', value.toString());
        editSegment[valueToChange] = value;
        break;
      default:
        this.editOptions.editElement(this.map, FeatureType.SEGMENT, id, valueToChange, value);
        editSegment[valueToChange] = value;
        break;
    }
    this.onUpdateSegment.next(editSegment);
  }

  async delete(): Promise<void> {
    if (this.selectedFeatureType === FeatureType.SEGMENT) {
      this.finishDeletion(this.deleteSelectedSegmentsInternal());
    } else if (this.selectedFeatureType === FeatureType.STOP) {
      await this.requestStopsDeletion(false);
    }
  }

  deleteBoxSelectedStops(): Promise<void> {
    this.hideContextMenus();
    return this.requestStopsDeletion(false);
  }

  deleteBoxSelectedSegments(): void {
    this.hideContextMenus();
    this.finishDeletion(this.deleteSelectedSegmentsInternal());
  }

  deleteBoxSelectedAll(): Promise<void> {
    this.hideContextMenus();
    return this.requestStopsDeletion(true);
  }

  // Stops used by other routes: a route stop is only disconnected (kept as town stop), a town stop cannot be deleted
  private async requestStopsDeletion(deleteSelectedSegmentsToo: boolean): Promise<void> {
    const blockedStops: IStopUsage[] = [];
    const disconnectStops: IStopUsage[] = [];
    const selectedStopIds = this.selectedStops.map((selectedStop) => Number(selectedStop.getProperty('id')));
    const routesByStop = await Promise.all(selectedStopIds.map((stopId) => this.getRoutesUsingStop(stopId)));
    selectedStopIds.forEach((stopId, index) => {
      const routes = routesByStop[index];
      const stop = this.stops.get(stopId) ?? this.townStops.get(stopId);
      if (!routes.length || !stop) return;
      (this.townStops.has(stopId) ? blockedStops : disconnectStops).push({ stop, routes });
    });
    const proceed = () => {
      const blockedStopIds = new Set(blockedStops.map((usage) => usage.stop.id));
      const disconnectStopIds = new Set(disconnectStops.map((usage) => usage.stop.id));
      const deletedSegments = deleteSelectedSegmentsToo ? this.deleteSelectedSegmentsInternal() : 0;
      this.finishDeletion(deletedSegments + this.deleteSelectedStopsInternal(blockedStopIds, disconnectStopIds));
    };
    if (blockedStops.length || disconnectStops.length) {
      this.onStopDeletionWarning.next({ blockedStops, disconnectStops, proceed });
    } else {
      proceed();
    }
  }

  private finishDeletion(deletedItems: number): void {
    if (deletedItems > 0) {
      this.startStop = null;
      this.endStop = null;
      this.loadSegmentAndStops().then(() => this.onDeleteFeature.next());
    }
  }

  private deleteSelectedSegmentsInternal(): number {
    let deletedItems = 0;
    this.somePoints = [];
    this.selectedSegments.forEach((segment) => {
      this.deleteOptions.removeElement(this.map, segment.getProperty('id'), FeatureType.SEGMENT);
      ++deletedItems;
    });
    this.selectedSegments = [];
    this.onSelectSegments.next([]);
    return deletedItems;
  }

  private deleteSelectedStopsInternal(blockedStopIds = new Set<number>(), disconnectStopIds = new Set<number>()): number {
    let deletedItems = 0;
    this.selectedStops.forEach((stop) => {
      const deletedId = Number(stop.getProperty('id'));
      if (blockedStopIds.has(deletedId)) return;
      if (this.townStops.has(deletedId) && !this.stops.has(deletedId)) {
        this.unmergeCounterpartOf(this.townStops.get(deletedId));
        this.removeTownStopFromMap(deletedId);
        ++deletedItems;
        return;
      }
      const deletedStop = this.stops.get(deletedId);
      const marker = this.stopsMarkers.get(deletedId);
      if (!deletedStop || !marker) return;
      const disconnectOnly = disconnectStopIds.has(deletedId);
      if (!disconnectOnly) this.unmergeCounterpartOf(deletedStop);
      this.pruneAlternativesReferencing(deletedStop, deletedId);
      if (deletedStop.connections?.length) {
        const connectedSegmentIds = new Set<number>(deletedStop.connections.map((connection: IBase) => connection.id));
        connectedSegmentIds.forEach((segmentId) => {
          this.deleteOptions.removeElement(this.map, segmentId, FeatureType.SEGMENT);
          this.segments.delete(segmentId);
        });
        this.selectedSegments = this.selectedSegments.filter((segment) => !connectedSegmentIds.has(Number(segment.getProperty('id'))));
      }
      if (disconnectOnly) {
        this.convertToTownStop(deletedId);
      } else {
        this.deleteOptions.removeElement(this.map, deletedId, FeatureType.STOP);
        marker.map = null;
        this.stopsMarkers.delete(deletedId);
        this.removeStopCircle(deletedId);
        this.stops.delete(deletedId);
      }
      ++deletedItems;
    });
    this.selectedStops = [];
    this.onSelectStops.next([]);
    this.onSelectSegments.next(this.selectedSegments);
    return deletedItems;
  }

  private unmergeCounterpartOf(deletedStop: any): void {
    if (!deletedStop?.bidirectional) return;
    const bidirectionalStopId = deletedStop.bidirectional.properties.id;
    const mergedStop = this.stops.get(bidirectionalStopId) ?? this.townStops.get(bidirectionalStopId);
    if (!mergedStop) return;
    const mergedStopFeatureType = this.stops.has(bidirectionalStopId) ? FeatureType.STOP : FeatureType.TOWN_STOP;
    mergedStop.merge = null;
    mergedStop.bidirectional = null;
    this.editOptions.editElement(this.map, mergedStopFeatureType, mergedStop.id, 'merge', null);
    this.editOptions.editElement(this.map, mergedStopFeatureType, mergedStop.id, 'bidirectional', null);
    this.reattachMarkerEvents(mergedStop.id, mergedStop);
    this.updateMarkerIcon(mergedStop.id, StopMarkerStatus.DEFAULT);
  }

  private pruneAlternativesReferencing(deletedStop: any, deletedId: number): void {
    if (!deletedStop.alternatives) return;
    deletedStop.alternatives = deletedStop.alternatives.filter((a: any) => a.properties.id !== deletedId);
    this.editOptions.editElement(this.map, FeatureType.STOP, deletedStop.id, 'alternatives', deletedStop.alternatives);
    this.stops.forEach((otherStop) => {
      if (otherStop.alternatives?.some((a: any) => a.properties.id === deletedId)) {
        const updatedAlternatives = otherStop.alternatives.filter((a: any) => a.properties.id !== deletedId);
        otherStop.alternatives = updatedAlternatives.length > 0 ? updatedAlternatives : null;
        this.editOptions.editElement(this.map, FeatureType.STOP, otherStop.id, 'alternatives', otherStop.alternatives);
      }
    });
  }

  public async restoreTownStopsFromMapData(): Promise<void> {
    this.townStopsMarkers.forEach((marker) => marker.map = null);
    this.townStopsMarkers.clear();
    this.townStops.clear();

    const AdvancedMarkerElement = await this.markerRenderService.getAdvancedMarkerElementClass();
    this.map.data.forEach((feature: any) => {
      if (feature.getProperty('type') !== FeatureType.TOWN_STOP) return;
      const id = feature.getProperty('id');
      if (this.stops.has(id)) return;
      const stop = this.getStopProperties(id, feature);
      this.townStops.set(id, stop);
      this.map.data.overrideStyle(feature, { visible: false });
      this.createMarkerSync(stop, true, true, this.townStopsMarkers, AdvancedMarkerElement);
    });
    this.applyTownStopsVisibility();
    this.onMapConfigChange.next(this.mapConfig);
  }

  private applyTownStopsVisibility(): void {
    this.townStopsMarkers.forEach((marker, id) => { marker.map = this.isTownStopVisible(id) ? this.map : null; });
  }

  private isTownStopVisible(id: number): boolean {
    return this.mapConfig.showAllStops || this.sessionStopIds.has(id);
  }

  public getFlightPathSnapshot(): IFlightPathSnapshot | null {
    if (this.creationSegments && this.coordinates.length > 0) {
      return {
        mode: 'segment',
        coordinates: this.coordinates.map((c: any) => ({ lat: c.lat, lng: c.lng })),
        somePoints: [...this.somePoints],
        startStop: this.startStop
          ? { id: this.startStop.id, lat: this.startStop.lat, lng: this.startStop.lng }
          : undefined,
        creationSegmentsType: this.creationSegmentsType,
      };
    }
    if (this.creationOutline && this.coordinates.length > 0) {
      return {
        mode: 'outline',
        coordinates: this.coordinates.map((c: any) => ({ lat: c.lat, lng: c.lng })),
        somePoints: [...this.somePoints],
        outlineColor: this.outlineColor,
      };
    }
    return null;
  }

  public restoreFlightPath(flightPath: IFlightPathSnapshot): void {
    this.drawnSegments.forEach((segment) => segment.setMap(null));
    this.drawnSegments = [];

    this.coordinates = [...flightPath.coordinates];
    this.somePoints = [...flightPath.somePoints];

    if (flightPath.mode === 'segment') {
      this.creationOutline = false;
      this.creationSegments = true;
      this.startStop = flightPath.startStop ?? null;
      if (flightPath.creationSegmentsType) {
        this.creationSegmentsType = flightPath.creationSegmentsType as CreationSegmentsType;
      }
      if (flightPath.startStop) {
        const startStopData = this.stops.get(flightPath.startStop.id);
        if (startStopData) {
          this.originStop = this.createOptions.buildPointFeature(
            flightPath.startStop.id,
            { lat: flightPath.startStop.lat, lng: flightPath.startStop.lng },
            startStopData
          );
        }
        const startStopFeature = this.utilsService.findFeatureByPropertyId(this.map.data, flightPath.startStop.id, FeatureType.STOP);
        if (startStopFeature) {
          this.selectedStops = [startStopFeature];
          this.updateMarkerIcon(flightPath.startStop.id, StopMarkerStatus.SELECTED);
          this.onSelectStops.next(this.selectedStops);
        }
      }
      this.activeCursor = this.creationSegmentsType === CreationSegmentsType.AUTO
        ? this.buildCursor(images.cursor.creationSegmentsAuto)
        : this.buildCursor(images.cursor.creationSegmentsManual);
      this.onCreationSegments.next(true);
    } else {
      this.creationSegments = false;
      this.creationOutline = true;
      this.outlineColor = flightPath.outlineColor;
      this.activeCursor = 'crosshair';
      this.onCreationOutline.next(true);
    }
    this.map.setOptions({ draggableCursor: this.activeCursor });
    this.forceCursor();

    if (this.coordinates.length > 1) {
      const polyline = new google.maps.Polyline({
        path: this.coordinates as any,
        geodesic: true,
        strokeColor: '#8136db',
        strokeOpacity: 1.0,
        strokeWeight: 4,
        zIndex: 2000
      });
      this.drawnSegments.push(polyline);
      polyline.setMap(this.map);
    }
  }

  private keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Shift' && !this.shiftPressed) {
      this.shiftPressed = true;
      this.setStopMarkersDraggable(false);
      this.activeCursor = 'default';
      this.map.setOptions({ draggableCursor: this.activeCursor });
      this.map.data.forEach((feature: any) => this.map.data.overrideStyle(feature, { cursor: this.activeCursor }));
      this.forceCursor();
    }
  };
  private keyupHandler = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this.shiftPressed = false;
      this.setStopMarkersDraggable(true);
      this.cancelBoxSelect();
      this.restoreCursor();
    }
  };
  private windowBlurHandler = () => {
    if (this.shiftPressed) {
      this.shiftPressed = false;
      this.setStopMarkersDraggable(true);
      this.restoreCursor();
    }
    this.cancelBoxSelect();
  };

  private loadMapConfig() {
    const stored = localStorage.getItem(APP.LOCAL_STORAGE_MAP_CONFIG);
    if (stored) {
      try {
        this.mapConfig = { ...this.defaultMapConfig, ...JSON.parse(stored), drivingSpeed: undefined };
      } catch {}
    }
  }

  public updateMapConfig<K extends keyof IMapConfig>(key: K, value: any, options?: any): void {
    this.mapConfig[key] = value;
    switch (key) {
      case 'mapView':
        this.map?.setMapTypeId(value);
        break;
      case 'showAllStops':
        if (options?.stops?.length) this.loadTownStops(options.stops);
        this.applyTownStopsVisibility();
        if (!value) {
          const selectedTownStopIds = this.selectedStops
            .map((stop) => Number(stop.getProperty('id')))
            .filter((id) => this.townStops.has(id) && !this.isTownStopVisible(id));
          if (selectedTownStopIds.length) this.clearSelectedStops();
          this.nameLabelsService.clear();
        }
        this.renderStopNames();
        break;
      case 'showRouteStops':
        this.toggleShowMarkers(this.stopsMarkers, value as boolean);
        this.applyStopCirclesVisibility();
        if (!value) this.clearSelectedStops();
        break;
      case 'showRadius':
        this.applyStopCirclesVisibility();
        break;
      case 'showStartStop':
        const startId = Array.from(this.stops.values()).find((stop) => stop.start)?.id;
        if (value) this.updateMarkerIcon(startId, StopMarkerStatus.START_SELECTED);
        else this.updateMarkerIcon(startId, StopMarkerStatus.DEFAULT);
        break;
      case 'showEndStop':
        const endId = Array.from(this.stops.values()).find((stop) => stop.end)?.id;
        if (value) this.updateMarkerIcon(endId, StopMarkerStatus.END_SELECTED);
        else this.updateMarkerIcon(endId, StopMarkerStatus.DEFAULT);
        break;
      case 'showSegments':
        this.map.data.forEach((feature) => {
          if (feature.getProperty('type') === FeatureType.SEGMENT) {
            this.map.data.overrideStyle(feature, { visible: value as boolean});
          }
        });
        if (!value) this.clearSelectedSegments();
        break;
      case 'showNames':
        if (value) this.renderStopNames();
        else this.nameLabelsService.clear();
        break;
    }
    this.onMapConfigChange.next(this.mapConfig);
    localStorage.setItem(APP.LOCAL_STORAGE_MAP_CONFIG, JSON.stringify(this.mapConfig));
  }

  public resetMapConfig() {
    let storedConfig: Partial<IMapConfig> = {};
    try {
      const stored = localStorage.getItem(APP.LOCAL_STORAGE_MAP_CONFIG);
      if (stored) storedConfig = JSON.parse(stored);
    } catch {}
    const baseConfig = { ...this.defaultMapConfig, ...storedConfig, drivingSpeed: undefined };
    (Object.keys(this.defaultMapConfig) as (keyof IMapConfig)[]).forEach((key) => {
      if (this.mapConfig[key] !== baseConfig[key]) {
        this.updateMapConfig(key, baseConfig[key]);
      }
    });
  }

  public applyMapConfig(map?: any, townStops?: any[], showAllStops?: boolean): void {
    if (map) this.map = map;
    this.map.setMapTypeId(this.mapConfig.mapView);
    if (!this.mapConfig.showRouteStops) {
      this.toggleShowMarkers(this.stopsMarkers, false);
    }
    this.applyStopCirclesVisibility();
    this.map.data.forEach((feature) => {
      const type = feature.getProperty('type');
      if (type === FeatureType.TOWN_STOP) {
        this.map.data.overrideStyle(feature, { visible: false });
      } else if (type === FeatureType.SEGMENT && !this.mapConfig.showSegments) {
        this.map.data.overrideStyle(feature, { visible: false });
      }
    });
    if (showAllStops) this.mapConfig.showAllStops = true;
    if (townStops?.length) this.townStopsLoading = this.loadTownStops(townStops);
    else this.applyTownStopsVisibility();
    this.onMapConfigChange.next(this.mapConfig);
  }

  // Town stops always live in the map data so every save submits them; "show all stops" only toggles their markers
  private async loadTownStops(townStopsFeatures: any[]): Promise<void> {
    if (this.mapConfig.showAllStops && !this.mapConfig.showRouteStops) {
      this.mapConfig.showRouteStops = true;
      this.toggleShowMarkers(this.stopsMarkers, true);
      this.applyStopCirclesVisibility();
    }
    const AdvancedMarkerElement = await this.markerRenderService.getAdvancedMarkerElementClass();
    this.townCatalogueLoaded = true;
    townStopsFeatures.forEach((feature: any) => {
      this.townCatalogueStopIds.add(feature.properties.id);
      const routeStop = this.stops.get(feature.properties.id);
      if (routeStop) {
        this.syncRouteStopWithTownStop(routeStop, this.getStopPropertiesFromObject(feature));
        return;
      }
      let stopProperties = this.getStopPropertiesFromObject(feature);
      stopProperties = {
        ...stopProperties,
        start: false,
        end: false,
        inflexion: false,
        type: FeatureType.TOWN_STOP
      };
      if (stopProperties.alternatives) delete stopProperties.alternatives;
      this.townStops.set(stopProperties.id, stopProperties);
      const existingFeature = this.utilsService.findFeatureByPropertyId(this.map.data, stopProperties.id, FeatureType.TOWN_STOP) as any;
      if (!existingFeature) {
        const dataFeature = new google.maps.Data.Feature({
          id: stopProperties.id,
          geometry: new google.maps.Data.Point({ lat: stopProperties.lat, lng: stopProperties.lng }),
          properties: stopProperties
        });
        this.map.data.add(dataFeature);
        this.map.data.overrideStyle(dataFeature, { visible: false });
      } else if (existingFeature.getGeometry().elements.lat() !== stopProperties.lat || existingFeature.getGeometry().elements.lng() !== stopProperties.lng) {
        existingFeature.setGeometry(new google.maps.LatLng(stopProperties.lat, stopProperties.lng));
      }
      const existingMarker = this.townStopsMarkers.get(stopProperties.id);
      if (!existingMarker) {
        this.createMarkerSync(stopProperties, true, this.canEditSharedStopFields(stopProperties.id), this.townStopsMarkers, AdvancedMarkerElement);
      } else {
        existingMarker.position = { lat: stopProperties.lat, lng: stopProperties.lng };
      }
    });
    this.applyTownStopsVisibility();
    try {
      await this.renderStopNames();
    } catch {}
    this.onMapConfigChange.next(this.mapConfig);
  }

  private toggleShowMarkers(markersMap: Map<number, google.maps.marker.AdvancedMarkerElement>, show: boolean) {
    markersMap.forEach((marker, markerId) => {
      if (!this.stops.has(markerId)) return;
      marker.map = show ? this.map : null;
    });
  }

}
